import React, { useState, useEffect, useCallback, useRef } from "react";
import { getOrFetchToken, getToken } from "../../hooks/useViewerToken";

const BASE_URL_DASH = "https://mis.empowerplus.in";

/* ── FILTER DEFINITIONS ── */
const FILTER_DEFS = [
    { key: "fy", label: "FY", type: "multi", apiKey: "fy" },
    { key: "date", label: "Date", type: "daterange" },
    { key: "fundingSource", label: "Funding Source", type: "multi", apiKey: "fundingSource" },
    { key: "scheme", label: "Scheme", type: "hierarchical", apiKey: "schemes" },
    { key: "project", label: "Project", type: "hierarchical", apiKey: "projects" },
    { key: "phase", label: "Phase", type: "multi", apiKey: "phases" },
    { key: "state", label: "State", type: "hierarchical", apiKey: "states" },
    { key: "district", label: "District", type: "hierarchical", apiKey: "districts" },
    { key: "centre", label: "Centre", type: "hierarchical", apiKey: "centers" },
    { key: "gender", label: "Gender", type: "multi", staticOptions: ["Male", "Female", "Transgender"] },
    { key: "sector", label: "Sector", type: "multi", apiKey: "sectors" },
];

const FLT_DEFAULT_OPEN = { fy: true, state: true };
const EMPTY_FILTERS = { fy: [], fundingSource: [], scheme: [], project: [], phase: [], state: [], district: [], centre: [], gender: [], sector: [] };

/* ── FILTER SIDEBAR ── */
function FilterGroup({ filter, open, onToggle, options, selected, onToggleValue }) {
    return (
        <div className="flt-group">
            <button className="flt-group-hd" onClick={onToggle}>
                <span className="flt-group-lbl">{filter.label}</span>
                <span className={`flt-chevron${open ? " open" : ""}`}>›</span>
            </button>
            {open && (
                <div className="flt-body">
                    {filter.type === "daterange" && (
                        <div className="flt-daterange">
                            <label>Start</label>
                            <input type="date" className="flt-date-input" />
                            <label>End</label>
                            <input type="date" className="flt-date-input" />
                        </div>
                    )}
                    {(filter.type === "multi" || filter.type === "hierarchical") && options.map(opt => (
                        <label key={opt} className="flt-check-row">
                            <input
                                type="checkbox"
                                className="flt-cb"
                                checked={selected.includes(opt)}
                                onChange={() => onToggleValue(filter.key, opt)}
                            />
                            <span>{opt}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

function FilterSidebar({ selected, onToggleValue, onApply, onReset, apiOptions }) {
    const [openFilters, setOpenFilters] = useState(FLT_DEFAULT_OPEN);

    const getOptions = (filter) => {
        if (filter.staticOptions) return filter.staticOptions;
        if (apiOptions && filter.apiKey && Array.isArray(apiOptions[filter.apiKey]))
            return apiOptions[filter.apiKey];
        return [];
    };

    const toggle = (key) => setOpenFilters(prev => ({ ...prev, [key]: !prev[key] }));
    const totalSelected = Object.values(selected).flat().length;

    return (
        <aside className="flt-sidebar">
            <div className="flt-header">
                <span className="flt-title">Filters {totalSelected > 0 && <span className="flt-badge">{totalSelected}</span>}</span>
                <button className="flt-reset" onClick={onReset}>Reset All</button>
            </div>
            <div className="flt-note">All filter views change according to selection.</div>
            {FILTER_DEFS.map(filter => (
                <FilterGroup
                    key={filter.key}
                    filter={filter}
                    open={!!openFilters[filter.key]}
                    onToggle={() => toggle(filter.key)}
                    options={getOptions(filter)}
                    selected={selected[filter.key] || []}
                    onToggleValue={onToggleValue}
                />
            ))}
            <button className="flt-apply" onClick={onApply}>Apply Filters</button>
            <style>{`
.flt-sidebar { width:256px; flex-shrink:0; background:#fff; border-left:1px solid #e5e7eb; padding:16px 14px 24px; min-height:100vh; position:sticky; top:0; max-height:100vh; overflow-y:auto; font-family:Inter,system-ui,sans-serif; }
.flt-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
.flt-title { font-size:14px; font-weight:900; color:#0f172a; display:flex; align-items:center; gap:6px; }
.flt-badge { background:#2563eb; color:#fff; font-size:10px; font-weight:800; border-radius:999px; padding:1px 6px; }
.flt-reset { font-size:11px; font-weight:700; color:#6b7280; background:none; border:1px solid #e5e7eb; border-radius:6px; padding:3px 8px; cursor:pointer; }
.flt-reset:hover { color:#ef4444; border-color:#fca5a5; }
.flt-note { font-size:10px; color:#9ca3af; line-height:1.4; background:#f8fafc; border-radius:6px; padding:6px 8px; margin-bottom:12px; }
.flt-group { border-bottom:1px solid #f1f5f9; }
.flt-group-hd { display:flex; align-items:center; justify-content:space-between; width:100%; background:none; border:none; padding:9px 2px; cursor:pointer; font-size:12px; font-weight:800; color:#374151; text-align:left; }
.flt-group-hd:hover { color:#2563eb; }
.flt-group-lbl { flex:1; }
.flt-chevron { font-size:14px; color:#9ca3af; transform:rotate(90deg); display:inline-block; transition:transform 0.2s; line-height:1; }
.flt-chevron.open { transform:rotate(270deg); }
.flt-body { padding:4px 0 10px 4px; display:flex; flex-direction:column; gap:4px; max-height:180px; overflow-y:auto; }
.flt-check-row { display:flex; align-items:center; gap:7px; font-size:12px; color:#4b5563; cursor:pointer; padding:2px 4px; border-radius:5px; }
.flt-check-row:hover { background:#eff6ff; color:#2563eb; }
.flt-cb { accent-color:#2563eb; width:13px; height:13px; flex-shrink:0; cursor:pointer; }
.flt-daterange { display:flex; flex-direction:column; gap:5px; font-size:11px; font-weight:700; color:#6b7280; }
.flt-date-input { border:1px solid #e5e7eb; border-radius:6px; padding:5px 7px; font-size:11px; color:#374151; outline:none; }
.flt-date-input:focus { border-color:#2563eb; }
.flt-apply { width:100%; margin-top:16px; background:#2563eb; color:#fff; border:none; border-radius:10px; padding:10px; font-size:13px; font-weight:800; cursor:pointer; }
.flt-apply:hover { background:#1d4ed8; }
            `}</style>
        </aside>
    );
}

/* ─── STATIC DATA ─── */

const AGE_DIST_DEFAULT = [
    { range: "Below 18", count: 0 },
    { range: "18 to 25", count: 0 },
    { range: "25 to 35", count: 0 },
    { range: "35+", count: 0 },
];


const TRAINING_ACTIVITIES = [
    { label: "Guest Lectures Organised", value: "142", sub: "Across all active centres", color: "#2563eb" },
    { label: "Alumni Interactions Organised", value: "68", sub: "Peer learning sessions", color: "#7c3aed" },
    { label: "Industry Visits Organised", value: "53", sub: "Factory / site visits", color: "#0e7490" },
];




const PROGRAM_EXP = [
    { label: "Training Satisfaction Score", value: "4.2 / 5", sub: "Avg rating from exit surveys", color: "#2563eb" },
    { label: "Recommendation Score", value: "82%", sub: "Would recommend to peers", color: "#059669" },
];

/* ─── HELPERS ─── */

function SectionBadge({ n, label }) {
    return (
        <div className="ys-section-hd">
            <span className="ys-section-badge">Section {n}</span>
            <span className="ys-section-title">{label}</span>
        </div>
    );
}

function StatCard({ label, value, sub, color }) {
    return (
        <div className="ys-card ys-stat" style={{ borderTop: `4px solid ${color}` }}>
            <div className="ys-stat-val" style={{ color }}>{value}</div>
            <div className="ys-stat-lbl">{label}</div>
            {sub && <div className="ys-stat-sub">{sub}</div>}
        </div>
    );
}

function VerticalBarChart({ data, nameKey = "name", valueKey = "count", height = 180, color1 = "#1e40af", color2 = "#60a5fa" }) {
    if (!data || data.length === 0) {
        return <div style={{ color: "#9ca3af", fontSize: 12, padding: "20px 0" }}>No data available</div>;
    }
    const max = Math.max(...data.map(d => d[valueKey]));
    const safeMax = max > 0 ? max : 1;
    return (
        <div className="ys-vbar-wrap" style={{ height }}>
            {data.map(d => (
                <div key={d[nameKey]} className="ys-vbar-item">
                    <div className="ys-vbar-val">{Number(d[valueKey]).toLocaleString()}</div>
                    <div className="ys-vbar-track">
                        <div className="ys-vbar-fill"
                            style={{
                                height: `${(d[valueKey] / safeMax) * 100}%`,
                                background: `linear-gradient(to top, ${color1}, ${color2})`,
                            }}
                        />
                    </div>
                    <div className="ys-vbar-name">{d[nameKey]}</div>
                </div>
            ))}
        </div>
    );
}

/* ─── PAGE ─── */

function buildFilterBody(selected) {
    return {
        fy: selected.fy || [],
        fundingSource: selected.fundingSource || [],
        schemes: selected.scheme || [],
        projects: selected.project || [],
        phases: selected.phase || [],
        states: selected.state || [],
        districts: selected.district || [],
        centers: selected.centre || [],
        gender: selected.gender || [],
        sectors: selected.sector || [],
        projectType: "Skilling",
    };
}

export default function YouthSkillingPage() {
    const [selected, setSelected] = useState(EMPTY_FILTERS);
    const [apiOptions, setApiOptions] = useState(null);
    const [demographics, setDemographics] = useState(null);
    const [ageDistribution, setAgeDistribution] = useState(AGE_DIST_DEFAULT);
    const [demoLoading, setDemoLoading] = useState(false);
    const [outreach, setOutreach] = useState(null);
    const [outreachLoading, setOutreachLoading] = useState(false);
    const [trainingOutcomes, setTrainingOutcomes] = useState(null);
    const [trainingOutcomesLoading, setTrainingOutcomesLoading] = useState(false);
    const [employmentOutcomes, setEmploymentOutcomes] = useState(null);
    const [employmentOutcomesLoading, setEmploymentOutcomesLoading] = useState(false);
    const [retention, setRetention] = useState(null);
    const [retentionLoading, setRetentionLoading] = useState(false);

    const fetchDemographics = (filters) => {
        const token = getToken();
        setDemoLoading(true);
        fetch(`${BASE_URL_DASH}/api/view-dashboard/youth-skilling/demographics`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(buildFilterBody(filters)),
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (data) {
                    setDemographics(data);
                    setAgeDistribution(
                        (data.ageDistribution || []).map(d => ({ range: d.range, count: Number(d.count) }))
                    );
                }
            })
            .catch(() => { })
            .finally(() => setDemoLoading(false));
    };

    const fetchOutreach = (filters) => {
        const token = getToken();
        setOutreachLoading(true);
        fetch(`${BASE_URL_DASH}/api/view-dashboard/youth-skilling/outreach-funnel`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(buildFilterBody(filters)),
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setOutreach(data); })
            .catch(() => { })
            .finally(() => setOutreachLoading(false));
    };

    const fetchTrainingOutcomes = (filters) => {
        const token = getToken();
        setTrainingOutcomesLoading(true);
        fetch(`${BASE_URL_DASH}/api/view-dashboard/youth-skilling/training-outcomes`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(buildFilterBody(filters)),
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setTrainingOutcomes(data); })
            .catch(() => { })
            .finally(() => setTrainingOutcomesLoading(false));
    };

    const fetchRetention = (filters) => {
        const token = getToken();
        setRetentionLoading(true);
        fetch(`${BASE_URL_DASH}/api/view-dashboard/youth-skilling/retention`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(buildFilterBody(filters)),
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setRetention(data); })
            .catch(() => { })
            .finally(() => setRetentionLoading(false));
    };

    const fetchEmploymentOutcomes = (filters) => {
        const token = getToken();
        setEmploymentOutcomesLoading(true);
        fetch(`${BASE_URL_DASH}/api/view-dashboard/youth-skilling/employment-outcomes`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(buildFilterBody(filters)),
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setEmploymentOutcomes(data); })
            .catch(() => { })
            .finally(() => setEmploymentOutcomesLoading(false));
    };

    const loadFilterOptions = useCallback((currentSelected) => {
        const token = localStorage.getItem("token") || sessionStorage.getItem("viewer_token");
        if (!token) return;
        const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
        fetch(`${BASE_URL_DASH}/api/view-dashboard/filter-options`, {
            method: "POST",
            headers: h,
            body: JSON.stringify(buildFilterBody(currentSelected)),
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setApiOptions(data); })
            .catch(() => {});
    }, []);

    useEffect(() => {
        (async () => {
            const token = await getOrFetchToken();
            if (!token) return;

            loadFilterOptions(EMPTY_FILTERS);
            fetchDemographics(EMPTY_FILTERS);
            fetchOutreach(EMPTY_FILTERS);
            fetchTrainingOutcomes(EMPTY_FILTERS);
            fetchEmploymentOutcomes(EMPTY_FILTERS);
            fetchRetention(EMPTY_FILTERS);
        })();
    }, [loadFilterOptions]);

    const filterDebounceRef = useRef(null);
    useEffect(() => {
        clearTimeout(filterDebounceRef.current);
        filterDebounceRef.current = setTimeout(() => {
            loadFilterOptions(selected);
        }, 300);
        return () => clearTimeout(filterDebounceRef.current);
    }, [selected, loadFilterOptions]);

    const handleToggleValue = (filterKey, value) => {
        setSelected(prev => {
            const current = prev[filterKey] || [];
            return {
                ...prev,
                [filterKey]: current.includes(value)
                    ? current.filter(v => v !== value)
                    : [...current, value],
            };
        });
    };

    const handleApply = () => {
        fetchDemographics(selected);
        fetchOutreach(selected);
        fetchTrainingOutcomes(selected);
        fetchEmploymentOutcomes(selected);
        fetchRetention(selected);
    };

    const handleReset = () => {
        const empty = EMPTY_FILTERS;
        setSelected(empty);
        fetchDemographics(empty);
        fetchOutreach(empty);
        fetchTrainingOutcomes(empty);
        fetchEmploymentOutcomes(empty);
        fetchRetention(empty);
    };

    return (
        <div className="ys-wrapper">

            {/* ── MAIN ── */}
            <div className="ys-main">

                {/* Page Header */}
                <div className="ys-page-header">
                    <div>
                        <div className="ys-breadcrumb">Impact Dimensions</div>
                        <h1 className="ys-title">Youth Skilling</h1>
                        <p className="ys-subtitle">End-to-end tracking — outreach, training, employment, and retention outcomes</p>
                    </div>
                    <div className="ys-badge">Section 1 · Live Data</div>
                </div>

                {/* ── SECTION 1: Demographics ── */}
                <SectionBadge n={1} label="Demographics" />
                {demoLoading ? (
                    <div style={{ padding: "18px 0", color: "#6b7280", fontSize: 13 }}>Loading demographics…</div>
                ) : (
                    <>
                        <div className="ys-cards-2">
                            <StatCard
                                label="Gender Split"
                                value={(() => {
                                    const gs = demographics && demographics.genderSplit;
                                    if (!gs || Object.keys(gs).length === 0) return "No data";
                                    const total = Object.values(gs).reduce((a, b) => a + b, 0);
                                    if (total === 0) return "No data";
                                    return Object.entries(gs)
                                        .map(([g, n]) => `${Math.round(n * 100 / total)}% ${g[0].toUpperCase()}`)
                                        .join(" / ");
                                })()}
                                sub="Male-female ratio across Skilling projects"
                                color="#2563eb"
                            />
                            <StatCard
                                label="Avg Household Income"
                                value={demographics && demographics.avgHouseholdIncome
                                    ? `₹${Number(demographics.avgHouseholdIncome).toLocaleString("en-IN")} / yr`
                                    : "No data"}
                                sub="Annual household income of beneficiaries"
                                color="#0e7490"
                            />
                        </div>
                        <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
                            <div className="ys-chart-title">Age Distribution of Beneficiaries</div>
                            <VerticalBarChart data={ageDistribution} nameKey="range" height={160} color1="#5b21b6" color2="#a78bfa" />
                        </div>
                    </>
                )}

                {/* ── SECTION 2: Outreach Funnel ── */}
                <SectionBadge n={2} label="Outreach Funnel" />
                {outreachLoading ? (
                    <div style={{ padding: "18px 0", color: "#6b7280", fontSize: 13 }}>Loading outreach data…</div>
                ) : (
                    <div className="ys-cards-4">
                        <StatCard
                            label="Candidates Mobilised"
                            value={outreach ? Number(outreach.candidatesMobilised).toLocaleString("en-IN") : "—"}
                            // sub="Total mobilized for Skilling projects"
                            color="#2563eb"
                        />
                        <StatCard
                            label="Candidates Counseled"
                            value={outreach ? Number(outreach.candidatesCounseled).toLocaleString("en-IN") : "—"}
                            // sub="Counselled = Yes in enrollment verification"
                            color="#7c3aed"
                        />
                        <StatCard
                            label="Candidates Enrolled"
                            value={outreach ? Number(outreach.candidatesEnrolled).toLocaleString("en-IN") : "—"}
                            // sub="Candidates registered in Skilling projects"
                            color="#059669"
                        />
                        <StatCard
                            label="Dropout Rate"
                            value={outreach ? `${outreach.dropoutRate}%` : "—"}
                            // sub="(Enrolled − Completed) ÷ Enrolled"
                            color="#ef4444"
                        />
                    </div>
                )}

                {/* ── SECTION 3: Training Activities ── */}
                {/* <SectionBadge n={3} label="Training Activities (HOI)" />
                <div className="ys-cards-3">
                    {TRAINING_ACTIVITIES.map(d => <StatCard key={d.label} {...d} />)}
                </div> */}

                {/* ── SECTION 4: Training Outcomes ── */}
                <SectionBadge n={4} label="Training Outcomes" />
                {trainingOutcomesLoading ? (
                    <div style={{ padding: "18px 0", color: "#6b7280", fontSize: 13 }}>Loading training outcomes…</div>
                ) : (
                    <>
                        <div className="ys-cards-4">
                            <StatCard
                                label="Candidates Trained"
                                value={trainingOutcomes ? Number(trainingOutcomes.candidatesTrained).toLocaleString("en-IN") : "—"}
                                // sub="Records in training completions (Skilling)"
                                color="#2563eb"
                            />
                            <StatCard
                                label="Candidates Assessed"
                                value={trainingOutcomes ? Number(trainingOutcomes.candidatesAssessed).toLocaleString("en-IN") : "—"}
                                // sub="Assessment status = Yes"
                                color="#7c3aed"
                            />
                            <StatCard
                                label="Candidates Certified"
                                value={trainingOutcomes ? Number(trainingOutcomes.candidatesCertified).toLocaleString("en-IN") : "—"}
                                // sub="Certified = true in certificates"
                                color="#059669"
                            />
                            <StatCard
                                label="Certification Rate"
                                value={trainingOutcomes ? `${trainingOutcomes.certificationRate}%` : "—"}
                                // sub="Certified ÷ Assessed"
                                color="#f59e0b"
                            />
                        </div>

                        {/* Top 5 Sectors chart */}
                        <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
                            <div className="ys-chart-row">
                                <div className="ys-sectors-kpi">
                                    <div className="ys-mini-label">Sectors Trained</div>
                                    <div className="ys-mini-val" style={{ color: "#0e7490" }}>
                                        {trainingOutcomes ? (trainingOutcomes.topSectors || []).length : "—"}
                                    </div>
                                    <div className="ys-mini-sub">Top sectors by completed training</div>
                                </div>
                                <div className="ys-divider-v" />
                                <div style={{ flex: 1 }}>
                                    <div className="ys-chart-title">Top 5 Sectors by Candidates Trained (Completed)</div>
                                    {trainingOutcomes && (trainingOutcomes.topSectors || []).length > 0 ? (
                                        <VerticalBarChart data={trainingOutcomes.topSectors} nameKey="name" height={190} />
                                    ) : (
                                        <div style={{ color: "#9ca3af", fontSize: 12, padding: "20px 0" }}>No sector data available</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* ── SECTION 5: Employment Outcomes ── */}
                <SectionBadge n={5} label="Employment Outcomes" />
                {employmentOutcomesLoading ? (
                    <div style={{ padding: "18px 0", color: "#6b7280", fontSize: 13 }}>Loading employment data…</div>
                ) : (
                    <>
                        <div className="ys-cards-4">
                            <StatCard
                                label="Candidates Placed"
                                value={employmentOutcomes ? Number(employmentOutcomes.candidatesPlaced).toLocaleString("en-IN") : "—"}
                                // sub="Placement status = true (Skilling)"
                                color="#2563eb"
                            />
                            <StatCard
                                label="Placement Rate"
                                value={employmentOutcomes ? `${employmentOutcomes.placementRate}%` : "—"}
                                // sub="Placed ÷ Trained"
                                color="#7c3aed"
                            />
                            <StatCard
                                label="Avg Salary at Placement"
                                value={employmentOutcomes && employmentOutcomes.avgSalaryAtPlacement
                                    ? `₹${Number(employmentOutcomes.avgSalaryAtPlacement).toLocaleString("en-IN")} / mo`
                                    : "No data"}
                                // sub="Average income per month at placement"
                                color="#059669"
                            />
                            <StatCard
                                label="Total Income Generated (Annual)"
                                value={employmentOutcomes ? `₹${employmentOutcomes.totalIncomeAnnualCr} Cr` : "—"}
                                // sub="Sum of monthly salaries × 12"
                                color="#f59e0b"
                            />
                        </div>

                        {/* Salary distribution bar chart */}
                        <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
                            <div className="ys-chart-title">Salary Distribution Brackets at Placement</div>
                            <div className="ys-chart-note">Categories: &lt;₹10K · ₹10–15K · ₹15–20K · ₹20–25K · &gt;₹25K</div>
                            <VerticalBarChart
                                data={employmentOutcomes ? (employmentOutcomes.salaryDistribution || []) : []}
                                nameKey="range"
                                height={160}
                                color1="#065f46"
                                color2="#34d399"
                            />
                        </div>

                        {/* Placed by sector bar chart */}
                        <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
                            <div className="ys-chart-title">Placed Beneficiaries by Job Sector (Top 6)</div>
                            {employmentOutcomes && (employmentOutcomes.placedBySector || []).length > 0 ? (
                                <VerticalBarChart
                                    data={employmentOutcomes.placedBySector}
                                    nameKey="name"
                                    height={170}
                                    color1="#7c2d12"
                                    color2="#fb923c"
                                />
                            ) : (
                                <div style={{ color: "#9ca3af", fontSize: 12, padding: "20px 0" }}>No sector data available</div>
                            )}
                        </div>
                    </>
                )}

                {/* ── SECTION 6: Retention & Career Progression ── */}
                <SectionBadge n={6} label="Retention & Career Progression" />
                {retentionLoading ? (
                    <div style={{ padding: "18px 0", color: "#6b7280", fontSize: 13 }}>Loading retention data…</div>
                ) : (
                    <>
                        <div className="ys-cards-4">
                            <StatCard
                                label="% Retained after 3 months"
                                value={retention ? `${retention.retainedAfter3m}%` : "—"}
                                // sub="Employed at 3m check-in ÷ Total placed"
                                color="#2563eb"
                            />
                            <StatCard
                                label="% Retained after 6 months"
                                value={retention ? `${retention.retainedAfter6m}%` : "—"}
                                // sub="Employed at 6m check-in ÷ Total placed"
                                color="#7c3aed"
                            />
                            <StatCard
                                label="% Retained after 12 months"
                                value={retention ? `${retention.retainedAfter12m}%` : "—"}
                                // sub="Employed at 12m check-in ÷ Total placed"
                                color="#059669"
                            />
                            {/* <StatCard
                                label="Avg Salary Growth after 1 yr"
                                value={retention ? `+${retention.avgSalaryGrowthPct}%` : "—"}
                                sub="Salary at 12m vs salary at placement"
                                color="#f59e0b"
                            /> */}
                        </div>
                        {/* <div className="ys-cards-1" style={{ marginTop: 12 }}>
                            <StatCard
                                label="% Promoted / Changed Role"
                                value={retention ? `${retention.promotedRate}%` : "—"}
                                sub="Changed employer/role at 12m ÷ Retained at 12m"
                                color="#0e7490"
                            />
                        </div> */}
                    </>
                )}

                {/* ── SECTION 7: Program Experience ── */}
                {/* <SectionBadge n={7} label="Program Experience" />
                <div className="ys-cards-2">
                    {PROGRAM_EXP.map(d => <StatCard key={d.label} {...d} />)}
                </div> */}

            </div>

            {/* ── FILTER SIDEBAR ── */}
            <FilterSidebar
                selected={selected}
                onToggleValue={handleToggleValue}
                onApply={handleApply}
                onReset={handleReset}
                apiOptions={apiOptions}
            />

            <style>{`
.ys-wrapper {
    display: flex;
    align-items: flex-start;
    background: #f8fafc;
    min-height: 100vh;
    font-family: Inter, system-ui, sans-serif;
    color: #111827;
}
.ys-main { flex: 1; min-width: 0; padding: 24px 28px; }

/* Page header */
.ys-page-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    margin-bottom: 20px;
}
.ys-breadcrumb { font-size: 12px; color: #6b7280; font-weight: 700; margin-bottom: 4px; }
.ys-title { font-size: 22px; font-weight: 900; color: #0f172a; margin: 0 0 4px; }
.ys-subtitle { font-size: 13px; color: #64748b; }
.ys-badge {
    background: #dcfce7; color: #166534;
    font-size: 11px; font-weight: 800; padding: 5px 12px;
    border-radius: 999px; white-space: nowrap;
}

/* Section header */
.ys-section-hd {
    display: flex; align-items: center; gap: 10px;
    margin: 22px 0 10px;
}
.ys-section-badge {
    background: #065f46; color: #fff;
    font-size: 10px; font-weight: 900; padding: 3px 9px;
    border-radius: 999px; white-space: nowrap; letter-spacing: 0.4px;
}
.ys-section-title {
    font-size: 14px; font-weight: 900; color: #0f172a;
}

/* Card grids */
.ys-cards-1 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 12px; }
.ys-cards-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.ys-cards-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.ys-cards-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }

/* Stat card */
.ys-card {
    background: #fff; border: 1px solid #e5e7eb;
    border-radius: 12px; padding: 16px 14px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}
.ys-stat { }
.ys-stat-val { font-size: 24px; font-weight: 900; line-height: 1; margin-bottom: 6px; }
.ys-stat-lbl { font-size: 12px; font-weight: 800; color: #374151; margin-bottom: 3px; }
.ys-stat-sub { font-size: 11px; color: #9ca3af; line-height: 1.3; }

/* Chart card */
.ys-chart-card { padding: 18px 20px; }
.ys-chart-title { font-size: 13px; font-weight: 800; color: #1e293b; margin-bottom: 4px; }
.ys-chart-note  { font-size: 11px; color: #9ca3af; margin-bottom: 14px; }

.ys-chart-row { display: flex; align-items: stretch; gap: 20px; }
.ys-sectors-kpi {
    display: flex; flex-direction: column; justify-content: center;
    align-items: center; width: 130px; flex-shrink: 0; text-align: center;
}
.ys-mini-label { font-size: 11px; font-weight: 800; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
.ys-mini-val   { font-size: 40px; font-weight: 900; line-height: 1; }
.ys-mini-sub   { font-size: 11px; color: #9ca3af; margin-top: 6px; }
.ys-divider-v  { width: 1px; background: #e5e7eb; flex-shrink: 0; }

/* Vertical bar chart */
.ys-vbar-wrap {
    display: flex; gap: 10px; align-items: flex-end; padding-bottom: 8px;
}
.ys-vbar-item {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; gap: 4px; height: 100%;
}
.ys-vbar-val  { font-size: 10px; font-weight: 800; color: #374151; }
.ys-vbar-track {
    flex: 1; width: 100%; max-width: 56px;
    background: #f1f5f9; border-radius: 6px 6px 0 0;
    display: flex; align-items: flex-end; overflow: hidden;
}
.ys-vbar-fill { width: 100%; border-radius: 6px 6px 0 0; transition: height 0.4s ease; }
.ys-vbar-name {
    font-size: 10px; font-weight: 700; color: #6b7280;
    text-align: center; line-height: 1.3; max-width: 72px;
}
            `}</style>
        </div>
    );
}


// import React, { useState, useEffect } from "react";

// const BASE_URL_DASH = "https://mis.empowerplus.in";

// /* ── FILTER DEFINITIONS ── */
// const FILTER_DEFS = [
//     { key: "fy", label: "FY", type: "multi", apiKey: "fy" },
//     { key: "date", label: "Date", type: "daterange" },
//     { key: "fundingSource", label: "Funding Source", type: "multi", apiKey: "fundingSource" },
//     { key: "scheme", label: "Scheme", type: "hierarchical", apiKey: "schemes" },
//     { key: "subScheme", label: "Sub Scheme", type: "hierarchical", apiKey: "subSchemes" },
//     { key: "project", label: "Project", type: "hierarchical", apiKey: "projects" },
//     { key: "phase", label: "Phase", type: "multi", apiKey: "phases" },
//     { key: "state", label: "State", type: "hierarchical", apiKey: "states" },
//     { key: "district", label: "District", type: "hierarchical", apiKey: "districts" },
//     { key: "centre", label: "Centre", type: "hierarchical", apiKey: "centers" },
//     { key: "funder", label: "Funder", type: "multi", apiKey: "funders" },
//     { key: "gender", label: "Gender", type: "multi", staticOptions: ["Male", "Female", "Transgender"] },
// ];

// const FLT_DEFAULT_OPEN = { fy: true, subScheme: true, state: true };
// const EMPTY_FILTERS = { fy: [], fundingSource: [], scheme: [], subScheme: [], project: [], phase: [], state: [], district: [], centre: [], funder: [], gender: [] };

// /* ── FILTER SIDEBAR ── */
// function FilterGroup({ filter, open, onToggle, options, selected, onToggleValue }) {
//     return (
//         <div className="flt-group">
//             <button className="flt-group-hd" onClick={onToggle}>
//                 <span className="flt-group-lbl">{filter.label}</span>
//                 <span className={`flt-chevron${open ? " open" : ""}`}>›</span>
//             </button>
//             {open && (
//                 <div className="flt-body">
//                     {filter.type === "daterange" && (
//                         <div className="flt-daterange">
//                             <label>Start</label>
//                             <input type="date" className="flt-date-input" />
//                             <label>End</label>
//                             <input type="date" className="flt-date-input" />
//                         </div>
//                     )}
//                     {(filter.type === "multi" || filter.type === "hierarchical") && options.map(opt => (
//                         <label key={opt} className="flt-check-row">
//                             <input
//                                 type="checkbox"
//                                 className="flt-cb"
//                                 checked={selected.includes(opt)}
//                                 onChange={() => onToggleValue(filter.key, opt)}
//                             />
//                             <span>{opt}</span>
//                         </label>
//                     ))}
//                 </div>
//             )}
//         </div>
//     );
// }

// function FilterSidebar({ selected, onToggleValue, onApply, onReset, apiOptions }) {
//     const [openFilters, setOpenFilters] = useState(FLT_DEFAULT_OPEN);

//     const getOptions = (filter) => {
//         if (filter.staticOptions) return filter.staticOptions;
//         if (apiOptions && filter.apiKey && Array.isArray(apiOptions[filter.apiKey]))
//             return apiOptions[filter.apiKey];
//         return [];
//     };

//     const toggle = (key) => setOpenFilters(prev => ({ ...prev, [key]: !prev[key] }));
//     const totalSelected = Object.values(selected).flat().length;

//     return (
//         <aside className="flt-sidebar">
//             <div className="flt-header">
//                 <span className="flt-title">Filters {totalSelected > 0 && <span className="flt-badge">{totalSelected}</span>}</span>
//                 <button className="flt-reset" onClick={onReset}>Reset All</button>
//             </div>
//             <div className="flt-note">All filter views change according to selection.</div>
//             {FILTER_DEFS.map(filter => (
//                 <FilterGroup
//                     key={filter.key}
//                     filter={filter}
//                     open={!!openFilters[filter.key]}
//                     onToggle={() => toggle(filter.key)}
//                     options={getOptions(filter)}
//                     selected={selected[filter.key] || []}
//                     onToggleValue={onToggleValue}
//                 />
//             ))}
//             <button className="flt-apply" onClick={onApply}>Apply Filters</button>
//             <style>{`
// .flt-sidebar { width:256px; flex-shrink:0; background:#fff; border-left:1px solid #e5e7eb; padding:16px 14px 24px; min-height:100vh; position:sticky; top:0; max-height:100vh; overflow-y:auto; font-family:Inter,system-ui,sans-serif; }
// .flt-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
// .flt-title { font-size:14px; font-weight:900; color:#0f172a; display:flex; align-items:center; gap:6px; }
// .flt-badge { background:#2563eb; color:#fff; font-size:10px; font-weight:800; border-radius:999px; padding:1px 6px; }
// .flt-reset { font-size:11px; font-weight:700; color:#6b7280; background:none; border:1px solid #e5e7eb; border-radius:6px; padding:3px 8px; cursor:pointer; }
// .flt-reset:hover { color:#ef4444; border-color:#fca5a5; }
// .flt-note { font-size:10px; color:#9ca3af; line-height:1.4; background:#f8fafc; border-radius:6px; padding:6px 8px; margin-bottom:12px; }
// .flt-group { border-bottom:1px solid #f1f5f9; }
// .flt-group-hd { display:flex; align-items:center; justify-content:space-between; width:100%; background:none; border:none; padding:9px 2px; cursor:pointer; font-size:12px; font-weight:800; color:#374151; text-align:left; }
// .flt-group-hd:hover { color:#2563eb; }
// .flt-group-lbl { flex:1; }
// .flt-chevron { font-size:14px; color:#9ca3af; transform:rotate(90deg); display:inline-block; transition:transform 0.2s; line-height:1; }
// .flt-chevron.open { transform:rotate(270deg); }
// .flt-body { padding:4px 0 10px 4px; display:flex; flex-direction:column; gap:4px; max-height:180px; overflow-y:auto; }
// .flt-check-row { display:flex; align-items:center; gap:7px; font-size:12px; color:#4b5563; cursor:pointer; padding:2px 4px; border-radius:5px; }
// .flt-check-row:hover { background:#eff6ff; color:#2563eb; }
// .flt-cb { accent-color:#2563eb; width:13px; height:13px; flex-shrink:0; cursor:pointer; }
// .flt-daterange { display:flex; flex-direction:column; gap:5px; font-size:11px; font-weight:700; color:#6b7280; }
// .flt-date-input { border:1px solid #e5e7eb; border-radius:6px; padding:5px 7px; font-size:11px; color:#374151; outline:none; }
// .flt-date-input:focus { border-color:#2563eb; }
// .flt-apply { width:100%; margin-top:16px; background:#2563eb; color:#fff; border:none; border-radius:10px; padding:10px; font-size:13px; font-weight:800; cursor:pointer; }
// .flt-apply:hover { background:#1d4ed8; }
//             `}</style>
//         </aside>
//     );
// }

// /* ─── STATIC DATA ─── */

// const AGE_DIST_DEFAULT = [
//     { range: "Below 18", count: 0 },
//     { range: "18 to 25", count: 0 },
//     { range: "25 to 35", count: 0 },
//     { range: "35+", count: 0 },
// ];


// const TRAINING_ACTIVITIES = [
//     { label: "Guest Lectures Organised", value: "142", sub: "Across all active centres", color: "#2563eb" },
//     { label: "Alumni Interactions Organised", value: "68", sub: "Peer learning sessions", color: "#7c3aed" },
//     { label: "Industry Visits Organised", value: "53", sub: "Factory / site visits", color: "#0e7490" },
// ];



// const RETENTION_ROW1 = [
//     { label: "% Retained after 3 months", value: "84%", sub: "Active at 3m ÷ Placed · see formula", color: "#2563eb" },
//     { label: "% Retained after 6 months", value: "71%", sub: "Active at 6m ÷ Placed", color: "#7c3aed" },
//     { label: "% Retained after 12 months", value: "58%", sub: "Active at 12m ÷ Placed", color: "#059669" },
//     { label: "Avg Salary Growth after 1 yr", value: "+22%", sub: "Salary at 1yr vs. joining salary", color: "#f59e0b" },
// ];

// const RETENTION_ROW2 = [
//     { label: "% Promoted / Added Responsibility", value: "34%", sub: "Of 12-month retained cohort", color: "#0e7490" },
// ];

// const PROGRAM_EXP = [
//     { label: "Training Satisfaction Score", value: "4.2 / 5", sub: "Avg rating from exit surveys", color: "#2563eb" },
//     { label: "Recommendation Score", value: "82%", sub: "Would recommend to peers", color: "#059669" },
// ];

// /* ─── HELPERS ─── */

// function SectionBadge({ n, label }) {
//     return (
//         <div className="ys-section-hd">
//             <span className="ys-section-badge">Section {n}</span>
//             <span className="ys-section-title">{label}</span>
//         </div>
//     );
// }

// function StatCard({ label, value, sub, color }) {
//     return (
//         <div className="ys-card ys-stat" style={{ borderTop: `4px solid ${color}` }}>
//             <div className="ys-stat-val" style={{ color }}>{value}</div>
//             <div className="ys-stat-lbl">{label}</div>
//             {sub && <div className="ys-stat-sub">{sub}</div>}
//         </div>
//     );
// }

// function VerticalBarChart({ data, nameKey = "name", valueKey = "count", height = 180, color1 = "#1e40af", color2 = "#60a5fa" }) {
//     if (!data || data.length === 0) {
//         return <div style={{ color: "#9ca3af", fontSize: 12, padding: "20px 0" }}>No data available</div>;
//     }
//     const max = Math.max(...data.map(d => d[valueKey]));
//     const safeMax = max > 0 ? max : 1;
//     return (
//         <div className="ys-vbar-wrap" style={{ height }}>
//             {data.map(d => (
//                 <div key={d[nameKey]} className="ys-vbar-item">
//                     <div className="ys-vbar-val">{Number(d[valueKey]).toLocaleString()}</div>
//                     <div className="ys-vbar-track">
//                         <div className="ys-vbar-fill"
//                             style={{
//                                 height: `${(d[valueKey] / safeMax) * 100}%`,
//                                 background: `linear-gradient(to top, ${color1}, ${color2})`,
//                             }}
//                         />
//                     </div>
//                     <div className="ys-vbar-name">{d[nameKey]}</div>
//                 </div>
//             ))}
//         </div>
//     );
// }

// /* ─── PAGE ─── */

// function buildFilterBody(selected) {
//     return {
//         fy: selected.fy || [],
//         fundingSource: selected.fundingSource || [],
//         schemes: selected.scheme || [],
//         subSchemes: selected.subScheme || [],
//         projects: selected.project || [],
//         phases: selected.phase || [],
//         states: selected.state || [],
//         districts: selected.district || [],
//         centers: selected.centre || [],
//         gender: selected.gender || [],
//     };
// }

// export default function YouthSkillingPage() {
//     const [selected, setSelected] = useState(EMPTY_FILTERS);
//     const [apiOptions, setApiOptions] = useState(null);
//     const [demographics, setDemographics] = useState(null);
//     const [ageDistribution, setAgeDistribution] = useState(AGE_DIST_DEFAULT);
//     const [demoLoading, setDemoLoading] = useState(false);
//     const [outreach, setOutreach] = useState(null);
//     const [outreachLoading, setOutreachLoading] = useState(false);
//     const [trainingOutcomes, setTrainingOutcomes] = useState(null);
//     const [trainingOutcomesLoading, setTrainingOutcomesLoading] = useState(false);
//     const [employmentOutcomes, setEmploymentOutcomes] = useState(null);
//     const [employmentOutcomesLoading, setEmploymentOutcomesLoading] = useState(false);

//     const fetchDemographics = (filters) => {
//         const token = localStorage.getItem("token");
//         setDemoLoading(true);
//         fetch(`${BASE_URL_DASH}/api/view-dashboard/youth-skilling/demographics`, {
//             method: "POST",
//             headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
//             body: JSON.stringify(buildFilterBody(filters)),
//         })
//             .then(r => r.ok ? r.json() : null)
//             .then(data => {
//                 if (data) {
//                     setDemographics(data);
//                     setAgeDistribution(
//                         (data.ageDistribution || []).map(d => ({ range: d.range, count: Number(d.count) }))
//                     );
//                 }
//             })
//             .catch(() => { })
//             .finally(() => setDemoLoading(false));
//     };

//     const fetchOutreach = (filters) => {
//         const token = localStorage.getItem("token");
//         setOutreachLoading(true);
//         fetch(`${BASE_URL_DASH}/api/view-dashboard/youth-skilling/outreach-funnel`, {
//             method: "POST",
//             headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
//             body: JSON.stringify(buildFilterBody(filters)),
//         })
//             .then(r => r.ok ? r.json() : null)
//             .then(data => { if (data) setOutreach(data); })
//             .catch(() => { })
//             .finally(() => setOutreachLoading(false));
//     };

//     const fetchTrainingOutcomes = (filters) => {
//         const token = localStorage.getItem("token");
//         setTrainingOutcomesLoading(true);
//         fetch(`${BASE_URL_DASH}/api/view-dashboard/youth-skilling/training-outcomes`, {
//             method: "POST",
//             headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
//             body: JSON.stringify(buildFilterBody(filters)),
//         })
//             .then(r => r.ok ? r.json() : null)
//             .then(data => { if (data) setTrainingOutcomes(data); })
//             .catch(() => { })
//             .finally(() => setTrainingOutcomesLoading(false));
//     };

//     const fetchEmploymentOutcomes = (filters) => {
//         const token = localStorage.getItem("token");
//         setEmploymentOutcomesLoading(true);
//         fetch(`${BASE_URL_DASH}/api/view-dashboard/youth-skilling/employment-outcomes`, {
//             method: "POST",
//             headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
//             body: JSON.stringify(buildFilterBody(filters)),
//         })
//             .then(r => r.ok ? r.json() : null)
//             .then(data => { if (data) setEmploymentOutcomes(data); })
//             .catch(() => { })
//             .finally(() => setEmploymentOutcomesLoading(false));
//     };

//     useEffect(() => {
//         const token = localStorage.getItem("token");
//         fetch(`${BASE_URL_DASH}/api/view-dashboard/filter-options`, {
//             headers: { Authorization: `Bearer ${token}` },
//         })
//             .then(r => r.ok ? r.json() : null)
//             .then(data => { if (data) setApiOptions(data); })
//             .catch(() => { });

//         fetchDemographics(EMPTY_FILTERS);
//         fetchOutreach(EMPTY_FILTERS);
//         fetchTrainingOutcomes(EMPTY_FILTERS);
//         fetchEmploymentOutcomes(EMPTY_FILTERS);
//     }, []);

//     const handleToggleValue = (filterKey, value) => {
//         setSelected(prev => {
//             const current = prev[filterKey] || [];
//             return {
//                 ...prev,
//                 [filterKey]: current.includes(value)
//                     ? current.filter(v => v !== value)
//                     : [...current, value],
//             };
//         });
//     };

//     const handleApply = () => {
//         fetchDemographics(selected);
//         fetchOutreach(selected);
//         fetchTrainingOutcomes(selected);
//         fetchEmploymentOutcomes(selected);
//     };

//     const handleReset = () => {
//         const empty = EMPTY_FILTERS;
//         setSelected(empty);
//         fetchDemographics(empty);
//         fetchOutreach(empty);
//         fetchTrainingOutcomes(empty);
//         fetchEmploymentOutcomes(empty);
//     };

//     return (
//         <div className="ys-wrapper">

//             {/* ── MAIN ── */}
//             <div className="ys-main">

//                 {/* Page Header */}
//                 <div className="ys-page-header">
//                     <div>
//                         <div className="ys-breadcrumb">Impact Dimensions</div>
//                         <h1 className="ys-title">Youth Skilling</h1>
//                         <p className="ys-subtitle">End-to-end tracking — outreach, training, employment, and retention outcomes</p>
//                     </div>
//                     <div className="ys-badge">Section 1 · Live Data</div>
//                 </div>

//                 {/* ── SECTION 1: Demographics ── */}
//                 <SectionBadge n={1} label="Demographics" />
//                 {demoLoading ? (
//                     <div style={{ padding: "18px 0", color: "#6b7280", fontSize: 13 }}>Loading demographics…</div>
//                 ) : (
//                     <>
//                         <div className="ys-cards-2">
//                             <StatCard
//                                 label="Gender Split"
//                                 value={demographics && demographics.genderSplit && Object.keys(demographics.genderSplit).length > 0
//                                     ? Object.entries(demographics.genderSplit)
//                                         .map(([g, n]) => `${g}: ${n.toLocaleString()}`)
//                                         .join(" / ")
//                                     : "No data"}
//                                 sub="Count by gender across Skilling projects"
//                                 color="#2563eb"
//                             />
//                             <StatCard
//                                 label="Avg Household Income"
//                                 value={demographics && demographics.avgHouseholdIncome
//                                     ? `₹${Number(demographics.avgHouseholdIncome).toLocaleString("en-IN")} / yr`
//                                     : "No data"}
//                                 sub="Annual household income of beneficiaries"
//                                 color="#0e7490"
//                             />
//                         </div>
//                         <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
//                             <div className="ys-chart-title">Age Distribution of Beneficiaries</div>
//                             <VerticalBarChart data={ageDistribution} nameKey="range" height={160} color1="#5b21b6" color2="#a78bfa" />
//                         </div>
//                     </>
//                 )}

//                 {/* ── SECTION 2: Outreach Funnel ── */}
//                 <SectionBadge n={2} label="Outreach Funnel" />
//                 {outreachLoading ? (
//                     <div style={{ padding: "18px 0", color: "#6b7280", fontSize: 13 }}>Loading outreach data…</div>
//                 ) : (
//                     <div className="ys-cards-4">
//                         <StatCard
//                             label="Candidates Mobilised"
//                             value={outreach ? Number(outreach.candidatesMobilised).toLocaleString("en-IN") : "—"}
//                             sub="Total mobilized for Skilling projects"
//                             color="#2563eb"
//                         />
//                         <StatCard
//                             label="Candidates Counseled"
//                             value={outreach ? Number(outreach.candidatesCounseled).toLocaleString("en-IN") : "—"}
//                             sub="Counselled = Yes in enrollment verification"
//                             color="#7c3aed"
//                         />
//                         <StatCard
//                             label="Candidates Enrolled"
//                             value={outreach ? Number(outreach.candidatesEnrolled).toLocaleString("en-IN") : "—"}
//                             sub="Candidates registered in Skilling projects"
//                             color="#059669"
//                         />
//                         <StatCard
//                             label="Dropout Rate"
//                             value={outreach ? `${outreach.dropoutRate}%` : "—"}
//                             sub="(Enrolled − Completed) ÷ Enrolled"
//                             color="#ef4444"
//                         />
//                     </div>
//                 )}

//                 {/* ── SECTION 3: Training Activities ── */}
//                 {/* <SectionBadge n={3} label="Training Activities (HOI)" />
//                 <div className="ys-cards-3">
//                     {TRAINING_ACTIVITIES.map(d => <StatCard key={d.label} {...d} />)}
//                 </div> */}

//                 {/* ── SECTION 4: Training Outcomes ── */}
//                 <SectionBadge n={4} label="Training Outcomes" />
//                 {trainingOutcomesLoading ? (
//                     <div style={{ padding: "18px 0", color: "#6b7280", fontSize: 13 }}>Loading training outcomes…</div>
//                 ) : (
//                     <>
//                         <div className="ys-cards-4">
//                             <StatCard
//                                 label="Candidates Trained"
//                                 value={trainingOutcomes ? Number(trainingOutcomes.candidatesTrained).toLocaleString("en-IN") : "—"}
//                                 sub="Records in training completions (Skilling)"
//                                 color="#2563eb"
//                             />
//                             <StatCard
//                                 label="Candidates Assessed"
//                                 value={trainingOutcomes ? Number(trainingOutcomes.candidatesAssessed).toLocaleString("en-IN") : "—"}
//                                 sub="Assessment status = Yes"
//                                 color="#7c3aed"
//                             />
//                             <StatCard
//                                 label="Candidates Certified"
//                                 value={trainingOutcomes ? Number(trainingOutcomes.candidatesCertified).toLocaleString("en-IN") : "—"}
//                                 sub="Certified = true in certificates"
//                                 color="#059669"
//                             />
//                             <StatCard
//                                 label="Certification Rate"
//                                 value={trainingOutcomes ? `${trainingOutcomes.certificationRate}%` : "—"}
//                                 sub="Certified ÷ Assessed"
//                                 color="#f59e0b"
//                             />
//                         </div>

//                         {/* Top 5 Sectors chart */}
//                         <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
//                             <div className="ys-chart-row">
//                                 <div className="ys-sectors-kpi">
//                                     <div className="ys-mini-label">Sectors Trained</div>
//                                     <div className="ys-mini-val" style={{ color: "#0e7490" }}>
//                                         {trainingOutcomes ? (trainingOutcomes.topSectors || []).length : "—"}
//                                     </div>
//                                     <div className="ys-mini-sub">Top sectors by completed training</div>
//                                 </div>
//                                 <div className="ys-divider-v" />
//                                 <div style={{ flex: 1 }}>
//                                     <div className="ys-chart-title">Top 5 Sectors by Candidates Trained (Completed)</div>
//                                     {trainingOutcomes && (trainingOutcomes.topSectors || []).length > 0 ? (
//                                         <VerticalBarChart data={trainingOutcomes.topSectors} nameKey="name" height={190} />
//                                     ) : (
//                                         <div style={{ color: "#9ca3af", fontSize: 12, padding: "20px 0" }}>No sector data available</div>
//                                     )}
//                                 </div>
//                             </div>
//                         </div>
//                     </>
//                 )}

//                 {/* ── SECTION 5: Employment Outcomes ── */}
//                 <SectionBadge n={5} label="Employment Outcomes" />
//                 {employmentOutcomesLoading ? (
//                     <div style={{ padding: "18px 0", color: "#6b7280", fontSize: 13 }}>Loading employment data…</div>
//                 ) : (
//                     <>
//                         <div className="ys-cards-4">
//                             <StatCard
//                                 label="Candidates Placed"
//                                 value={employmentOutcomes ? Number(employmentOutcomes.candidatesPlaced).toLocaleString("en-IN") : "—"}
//                                 sub="Placement status = true (Skilling)"
//                                 color="#2563eb"
//                             />
//                             <StatCard
//                                 label="Placement Rate"
//                                 value={employmentOutcomes ? `${employmentOutcomes.placementRate}%` : "—"}
//                                 sub="Placed ÷ Trained"
//                                 color="#7c3aed"
//                             />
//                             <StatCard
//                                 label="Avg Salary at Placement"
//                                 value={employmentOutcomes && employmentOutcomes.avgSalaryAtPlacement
//                                     ? `₹${Number(employmentOutcomes.avgSalaryAtPlacement).toLocaleString("en-IN")} / mo`
//                                     : "No data"}
//                                 sub="Average income per month at placement"
//                                 color="#059669"
//                             />
//                             <StatCard
//                                 label="Total Income Generated (Annual)"
//                                 value={employmentOutcomes ? `₹${employmentOutcomes.totalIncomeAnnualCr} Cr` : "—"}
//                                 sub="Sum of monthly salaries × 12"
//                                 color="#f59e0b"
//                             />
//                         </div>

//                         {/* Salary distribution bar chart */}
//                         <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
//                             <div className="ys-chart-title">Salary Distribution Brackets at Placement</div>
//                             <div className="ys-chart-note">Categories: &lt;₹10K · ₹10–15K · ₹15–20K · ₹20–25K · &gt;₹25K</div>
//                             <VerticalBarChart
//                                 data={employmentOutcomes ? (employmentOutcomes.salaryDistribution || []) : []}
//                                 nameKey="range"
//                                 height={160}
//                                 color1="#065f46"
//                                 color2="#34d399"
//                             />
//                         </div>

//                         {/* Placed by sector bar chart */}
//                         <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
//                             <div className="ys-chart-title">Placed Beneficiaries by Job Sector (Top 6)</div>
//                             {employmentOutcomes && (employmentOutcomes.placedBySector || []).length > 0 ? (
//                                 <VerticalBarChart
//                                     data={employmentOutcomes.placedBySector}
//                                     nameKey="name"
//                                     height={170}
//                                     color1="#7c2d12"
//                                     color2="#fb923c"
//                                 />
//                             ) : (
//                                 <div style={{ color: "#9ca3af", fontSize: 12, padding: "20px 0" }}>No sector data available</div>
//                             )}
//                         </div>
//                     </>
//                 )}

//                 {/* ── SECTION 6: Retention & Career Progression ── */}
//                 <SectionBadge n={6} label="Retention & Career Progression" />
//                 <div className="ys-cards-4">
//                     {RETENTION_ROW1.map(d => <StatCard key={d.label} {...d} />)}
//                 </div>
//                 <div className="ys-cards-1" style={{ marginTop: 12 }}>
//                     {RETENTION_ROW2.map(d => <StatCard key={d.label} {...d} />)}
//                 </div>

//                 {/* ── SECTION 7: Program Experience ── */}
//                 <SectionBadge n={7} label="Program Experience" />
//                 <div className="ys-cards-2">
//                     {PROGRAM_EXP.map(d => <StatCard key={d.label} {...d} />)}
//                 </div>

//             </div>

//             {/* ── FILTER SIDEBAR ── */}
//             <FilterSidebar
//                 selected={selected}
//                 onToggleValue={handleToggleValue}
//                 onApply={handleApply}
//                 onReset={handleReset}
//                 apiOptions={apiOptions}
//             />

//             <style>{`
// .ys-wrapper {
//     display: flex;
//     align-items: flex-start;
//     background: #f8fafc;
//     min-height: 100vh;
//     font-family: Inter, system-ui, sans-serif;
//     color: #111827;
// }
// .ys-main { flex: 1; min-width: 0; padding: 24px 28px; }

// /* Page header */
// .ys-page-header {
//     display: flex; justify-content: space-between; align-items: flex-start;
//     margin-bottom: 20px;
// }
// .ys-breadcrumb { font-size: 12px; color: #6b7280; font-weight: 700; margin-bottom: 4px; }
// .ys-title { font-size: 22px; font-weight: 900; color: #0f172a; margin: 0 0 4px; }
// .ys-subtitle { font-size: 13px; color: #64748b; }
// .ys-badge {
//     background: #dcfce7; color: #166534;
//     font-size: 11px; font-weight: 800; padding: 5px 12px;
//     border-radius: 999px; white-space: nowrap;
// }

// /* Section header */
// .ys-section-hd {
//     display: flex; align-items: center; gap: 10px;
//     margin: 22px 0 10px;
// }
// .ys-section-badge {
//     background: #065f46; color: #fff;
//     font-size: 10px; font-weight: 900; padding: 3px 9px;
//     border-radius: 999px; white-space: nowrap; letter-spacing: 0.4px;
// }
// .ys-section-title {
//     font-size: 14px; font-weight: 900; color: #0f172a;
// }

// /* Card grids */
// .ys-cards-1 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 12px; }
// .ys-cards-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
// .ys-cards-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
// .ys-cards-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }

// /* Stat card */
// .ys-card {
//     background: #fff; border: 1px solid #e5e7eb;
//     border-radius: 12px; padding: 16px 14px;
//     box-shadow: 0 1px 3px rgba(0,0,0,0.05);
// }
// .ys-stat { }
// .ys-stat-val { font-size: 24px; font-weight: 900; line-height: 1; margin-bottom: 6px; }
// .ys-stat-lbl { font-size: 12px; font-weight: 800; color: #374151; margin-bottom: 3px; }
// .ys-stat-sub { font-size: 11px; color: #9ca3af; line-height: 1.3; }

// /* Chart card */
// .ys-chart-card { padding: 18px 20px; }
// .ys-chart-title { font-size: 13px; font-weight: 800; color: #1e293b; margin-bottom: 4px; }
// .ys-chart-note  { font-size: 11px; color: #9ca3af; margin-bottom: 14px; }

// .ys-chart-row { display: flex; align-items: stretch; gap: 20px; }
// .ys-sectors-kpi {
//     display: flex; flex-direction: column; justify-content: center;
//     align-items: center; width: 130px; flex-shrink: 0; text-align: center;
// }
// .ys-mini-label { font-size: 11px; font-weight: 800; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
// .ys-mini-val   { font-size: 40px; font-weight: 900; line-height: 1; }
// .ys-mini-sub   { font-size: 11px; color: #9ca3af; margin-top: 6px; }
// .ys-divider-v  { width: 1px; background: #e5e7eb; flex-shrink: 0; }

// /* Vertical bar chart */
// .ys-vbar-wrap {
//     display: flex; gap: 10px; align-items: flex-end; padding-bottom: 8px;
// }
// .ys-vbar-item {
//     flex: 1; display: flex; flex-direction: column;
//     align-items: center; gap: 4px; height: 100%;
// }
// .ys-vbar-val  { font-size: 10px; font-weight: 800; color: #374151; }
// .ys-vbar-track {
//     flex: 1; width: 100%; max-width: 56px;
//     background: #f1f5f9; border-radius: 6px 6px 0 0;
//     display: flex; align-items: flex-end; overflow: hidden;
// }
// .ys-vbar-fill { width: 100%; border-radius: 6px 6px 0 0; transition: height 0.4s ease; }
// .ys-vbar-name {
//     font-size: 10px; font-weight: 700; color: #6b7280;
//     text-align: center; line-height: 1.3; max-width: 72px;
// }
//             `}</style>
//         </div>
//     );
// }






// // import React, { useState, useEffect } from "react";

// // const BASE_URL_DASH = "https://mis.empowerplus.in";

// // /* ── FILTER DEFINITIONS ── */
// // const FILTER_DEFS = [
// //     { key: "fy", label: "FY", type: "multi", apiKey: "fy" },
// //     { key: "date", label: "Date", type: "daterange" },
// //     { key: "fundingSource", label: "Funding Source", type: "multi", apiKey: "fundingSource" },
// //     { key: "scheme", label: "Scheme", type: "hierarchical", apiKey: "schemes" },
// //     { key: "subScheme", label: "Sub Scheme", type: "hierarchical", apiKey: "subSchemes" },
// //     { key: "project", label: "Project", type: "hierarchical", apiKey: "projects" },
// //     { key: "phase", label: "Phase", type: "multi", apiKey: "phases" },
// //     { key: "state", label: "State", type: "hierarchical", apiKey: "states" },
// //     { key: "district", label: "District", type: "hierarchical", apiKey: "districts" },
// //     { key: "centre", label: "Centre", type: "hierarchical", apiKey: "centers" },
// //     { key: "funder", label: "Funder", type: "multi", apiKey: "funders" },
// //     { key: "gender", label: "Gender", type: "multi", staticOptions: ["Male", "Female", "Transgender"] },
// // ];

// // const FLT_DEFAULT_OPEN = { fy: true, subScheme: true, state: true };
// // const EMPTY_FILTERS = { fy: [], fundingSource: [], scheme: [], subScheme: [], project: [], phase: [], state: [], district: [], centre: [], funder: [], gender: [] };

// // /* ── FILTER SIDEBAR ── */
// // function FilterGroup({ filter, open, onToggle, options, selected, onToggleValue }) {
// //     return (
// //         <div className="flt-group">
// //             <button className="flt-group-hd" onClick={onToggle}>
// //                 <span className="flt-group-lbl">{filter.label}</span>
// //                 <span className={`flt-chevron${open ? " open" : ""}`}>›</span>
// //             </button>
// //             {open && (
// //                 <div className="flt-body">
// //                     {filter.type === "daterange" && (
// //                         <div className="flt-daterange">
// //                             <label>Start</label>
// //                             <input type="date" className="flt-date-input" />
// //                             <label>End</label>
// //                             <input type="date" className="flt-date-input" />
// //                         </div>
// //                     )}
// //                     {(filter.type === "multi" || filter.type === "hierarchical") && options.map(opt => (
// //                         <label key={opt} className="flt-check-row">
// //                             <input
// //                                 type="checkbox"
// //                                 className="flt-cb"
// //                                 checked={selected.includes(opt)}
// //                                 onChange={() => onToggleValue(filter.key, opt)}
// //                             />
// //                             <span>{opt}</span>
// //                         </label>
// //                     ))}
// //                 </div>
// //             )}
// //         </div>
// //     );
// // }

// // function FilterSidebar({ selected, onToggleValue, onApply, onReset, apiOptions }) {
// //     const [openFilters, setOpenFilters] = useState(FLT_DEFAULT_OPEN);

// //     const getOptions = (filter) => {
// //         if (filter.staticOptions) return filter.staticOptions;
// //         if (apiOptions && filter.apiKey && Array.isArray(apiOptions[filter.apiKey]))
// //             return apiOptions[filter.apiKey];
// //         return [];
// //     };

// //     const toggle = (key) => setOpenFilters(prev => ({ ...prev, [key]: !prev[key] }));
// //     const totalSelected = Object.values(selected).flat().length;

// //     return (
// //         <aside className="flt-sidebar">
// //             <div className="flt-header">
// //                 <span className="flt-title">Filters {totalSelected > 0 && <span className="flt-badge">{totalSelected}</span>}</span>
// //                 <button className="flt-reset" onClick={onReset}>Reset All</button>
// //             </div>
// //             <div className="flt-note">All filter views change according to selection.</div>
// //             {FILTER_DEFS.map(filter => (
// //                 <FilterGroup
// //                     key={filter.key}
// //                     filter={filter}
// //                     open={!!openFilters[filter.key]}
// //                     onToggle={() => toggle(filter.key)}
// //                     options={getOptions(filter)}
// //                     selected={selected[filter.key] || []}
// //                     onToggleValue={onToggleValue}
// //                 />
// //             ))}
// //             <button className="flt-apply" onClick={onApply}>Apply Filters</button>
// //             <style>{`
// // .flt-sidebar { width:256px; flex-shrink:0; background:#fff; border-left:1px solid #e5e7eb; padding:16px 14px 24px; min-height:100vh; position:sticky; top:0; max-height:100vh; overflow-y:auto; font-family:Inter,system-ui,sans-serif; }
// // .flt-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
// // .flt-title { font-size:14px; font-weight:900; color:#0f172a; display:flex; align-items:center; gap:6px; }
// // .flt-badge { background:#2563eb; color:#fff; font-size:10px; font-weight:800; border-radius:999px; padding:1px 6px; }
// // .flt-reset { font-size:11px; font-weight:700; color:#6b7280; background:none; border:1px solid #e5e7eb; border-radius:6px; padding:3px 8px; cursor:pointer; }
// // .flt-reset:hover { color:#ef4444; border-color:#fca5a5; }
// // .flt-note { font-size:10px; color:#9ca3af; line-height:1.4; background:#f8fafc; border-radius:6px; padding:6px 8px; margin-bottom:12px; }
// // .flt-group { border-bottom:1px solid #f1f5f9; }
// // .flt-group-hd { display:flex; align-items:center; justify-content:space-between; width:100%; background:none; border:none; padding:9px 2px; cursor:pointer; font-size:12px; font-weight:800; color:#374151; text-align:left; }
// // .flt-group-hd:hover { color:#2563eb; }
// // .flt-group-lbl { flex:1; }
// // .flt-chevron { font-size:14px; color:#9ca3af; transform:rotate(90deg); display:inline-block; transition:transform 0.2s; line-height:1; }
// // .flt-chevron.open { transform:rotate(270deg); }
// // .flt-body { padding:4px 0 10px 4px; display:flex; flex-direction:column; gap:4px; max-height:180px; overflow-y:auto; }
// // .flt-check-row { display:flex; align-items:center; gap:7px; font-size:12px; color:#4b5563; cursor:pointer; padding:2px 4px; border-radius:5px; }
// // .flt-check-row:hover { background:#eff6ff; color:#2563eb; }
// // .flt-cb { accent-color:#2563eb; width:13px; height:13px; flex-shrink:0; cursor:pointer; }
// // .flt-daterange { display:flex; flex-direction:column; gap:5px; font-size:11px; font-weight:700; color:#6b7280; }
// // .flt-date-input { border:1px solid #e5e7eb; border-radius:6px; padding:5px 7px; font-size:11px; color:#374151; outline:none; }
// // .flt-date-input:focus { border-color:#2563eb; }
// // .flt-apply { width:100%; margin-top:16px; background:#2563eb; color:#fff; border:none; border-radius:10px; padding:10px; font-size:13px; font-weight:800; cursor:pointer; }
// // .flt-apply:hover { background:#1d4ed8; }
// //             `}</style>
// //         </aside>
// //     );
// // }

// // /* ─── STATIC DATA ─── */

// // const AGE_DIST_DEFAULT = [
// //     { range: "Below 18", count: 0 },
// //     { range: "18 to 25", count: 0 },
// //     { range: "25 to 35", count: 0 },
// //     { range: "35+", count: 0 },
// // ];

// // const OUTREACH = [
// //     { label: "Candidates Mobilised", value: "24,500", sub: "Total outreach contacts", color: "#2563eb" },
// //     { label: "Candidates Counseled", value: "18,200", sub: "74.3% of mobilised", color: "#7c3aed" },
// //     { label: "Candidates Enrolled", value: "15,600", sub: "85.7% of counseled", color: "#059669" },
// //     { label: "Dropout Rate", value: "8.4%", sub: "Post-enrolment dropout", color: "#ef4444" },
// // ];

// // const TRAINING_ACTIVITIES = [
// //     { label: "Guest Lectures Organised", value: "142", sub: "Across all active centres", color: "#2563eb" },
// //     { label: "Alumni Interactions Organised", value: "68", sub: "Peer learning sessions", color: "#7c3aed" },
// //     { label: "Industry Visits Organised", value: "53", sub: "Factory / site visits", color: "#0e7490" },
// // ];

// // const TRAINING_OUTCOMES_CARDS = [
// //     { label: "Candidates Trained", value: "14,380", sub: "Completed full training", color: "#2563eb" },
// //     { label: "Candidates Assessed", value: "13,210", sub: "91.9% of trained", color: "#7c3aed" },
// //     { label: "Candidates Certified", value: "11,890", sub: "Passed assessment", color: "#059669" },
// //     { label: "Certification Rate", value: "89.6%", sub: "Certified ÷ Assessed", color: "#f59e0b" },
// // ];

// // const SECTORS_TRAINED = { label: "Sectors Trained", value: "12", sub: "Distinct job sectors covered", color: "#0e7490" };

// // const JOB_ROLES_DATA = [
// //     { name: "Electrician", count: 2840 },
// //     { name: "Welder", count: 2310 },
// //     { name: "BFSI Executive", count: 1980 },
// //     { name: "Retail Associate", count: 1760 },
// //     { name: "IT Support", count: 1450 },
// //     { name: "Hospitality", count: 1240 },
// //     { name: "Solar Technician", count: 980 },
// //     { name: "Healthcare Aid", count: 820 },
// // ];

// // const EMPLOYMENT_CARDS = [
// //     { label: "Candidates Placed", value: "9,840", sub: "Successfully employed", color: "#2563eb" },
// //     { label: "Placement Rate", value: "68.4%", sub: "Placed ÷ Trained", color: "#7c3aed" },
// //     { label: "Avg Salary at Placement", value: "₹14,200", sub: "Per month at joining", color: "#059669" },
// //     { label: "Total Income Generated (Annual)", value: "₹168 Cr", sub: "Aggregate annual income of placed", color: "#f59e0b" },
// // ];

// // const SALARY_DIST = [
// //     { range: "< ₹10K", count: 820 },
// //     { range: "₹10–15K", count: 3240 },
// //     { range: "₹15–20K", count: 3680 },
// //     { range: "₹20–25K", count: 1620 },
// //     { range: "> ₹25K", count: 480 },
// // ];

// // const JOB_SECTORS = [
// //     { name: "Manufacturing", count: 2840 },
// //     { name: "Retail", count: 2190 },
// //     { name: "IT / ITES", count: 1680 },
// //     { name: "Construction", count: 1340 },
// //     { name: "Healthcare", count: 980 },
// //     { name: "Hospitality", count: 810 },
// // ];

// // const RETENTION_ROW1 = [
// //     { label: "% Retained after 3 months", value: "84%", sub: "Active at 3m ÷ Placed · see formula", color: "#2563eb" },
// //     { label: "% Retained after 6 months", value: "71%", sub: "Active at 6m ÷ Placed", color: "#7c3aed" },
// //     { label: "% Retained after 12 months", value: "58%", sub: "Active at 12m ÷ Placed", color: "#059669" },
// //     { label: "Avg Salary Growth after 1 yr", value: "+22%", sub: "Salary at 1yr vs. joining salary", color: "#f59e0b" },
// // ];

// // const RETENTION_ROW2 = [
// //     { label: "% Promoted / Added Responsibility", value: "34%", sub: "Of 12-month retained cohort", color: "#0e7490" },
// // ];

// // const PROGRAM_EXP = [
// //     { label: "Training Satisfaction Score", value: "4.2 / 5", sub: "Avg rating from exit surveys", color: "#2563eb" },
// //     { label: "Recommendation Score", value: "82%", sub: "Would recommend to peers", color: "#059669" },
// // ];

// // /* ─── HELPERS ─── */

// // function SectionBadge({ n, label }) {
// //     return (
// //         <div className="ys-section-hd">
// //             <span className="ys-section-badge">Section {n}</span>
// //             <span className="ys-section-title">{label}</span>
// //         </div>
// //     );
// // }

// // function StatCard({ label, value, sub, color }) {
// //     return (
// //         <div className="ys-card ys-stat" style={{ borderTop: `4px solid ${color}` }}>
// //             <div className="ys-stat-val" style={{ color }}>{value}</div>
// //             <div className="ys-stat-lbl">{label}</div>
// //             {sub && <div className="ys-stat-sub">{sub}</div>}
// //         </div>
// //     );
// // }

// // function VerticalBarChart({ data, nameKey = "name", valueKey = "count", height = 180, color1 = "#1e40af", color2 = "#60a5fa" }) {
// //     const max = Math.max(...data.map(d => d[valueKey]));
// //     return (
// //         <div className="ys-vbar-wrap" style={{ height }}>
// //             {data.map(d => (
// //                 <div key={d[nameKey]} className="ys-vbar-item">
// //                     <div className="ys-vbar-val">{d[valueKey].toLocaleString()}</div>
// //                     <div className="ys-vbar-track">
// //                         <div className="ys-vbar-fill"
// //                             style={{
// //                                 height: `${(d[valueKey] / max) * 100}%`,
// //                                 background: `linear-gradient(to top, ${color1}, ${color2})`,
// //                             }}
// //                         />
// //                     </div>
// //                     <div className="ys-vbar-name">{d[nameKey]}</div>
// //                 </div>
// //             ))}
// //         </div>
// //     );
// // }

// // /* ─── PAGE ─── */

// // function buildFilterBody(selected) {
// //     return {
// //         fy: selected.fy || [],
// //         fundingSource: selected.fundingSource || [],
// //         schemes: selected.scheme || [],
// //         subSchemes: selected.subScheme || [],
// //         projects: selected.project || [],
// //         phases: selected.phase || [],
// //         states: selected.state || [],
// //         districts: selected.district || [],
// //         centers: selected.centre || [],
// //         gender: selected.gender || [],
// //     };
// // }

// // export default function YouthSkillingPage() {
// //     const [selected, setSelected] = useState(EMPTY_FILTERS);
// //     const [apiOptions, setApiOptions] = useState(null);
// //     const [demographics, setDemographics] = useState(null);
// //     const [ageDistribution, setAgeDistribution] = useState(AGE_DIST_DEFAULT);
// //     const [demoLoading, setDemoLoading] = useState(false);

// //     const fetchDemographics = (filters) => {
// //         const token = localStorage.getItem("token");
// //         setDemoLoading(true);
// //         fetch(`${BASE_URL_DASH}/api/view-dashboard/youth-skilling/demographics`, {
// //             method: "POST",
// //             headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
// //             body: JSON.stringify(buildFilterBody(filters)),
// //         })
// //             .then(r => r.ok ? r.json() : null)
// //             .then(data => {
// //                 if (data) {
// //                     setDemographics(data);
// //                     setAgeDistribution(
// //                         (data.ageDistribution || []).map(d => ({ range: d.range, count: Number(d.count) }))
// //                     );
// //                 }
// //             })
// //             .catch(() => { })
// //             .finally(() => setDemoLoading(false));
// //     };

// //     useEffect(() => {
// //         const token = localStorage.getItem("token");
// //         fetch(`${BASE_URL_DASH}/api/view-dashboard/filter-options`, {
// //             headers: { Authorization: `Bearer ${token}` },
// //         })
// //             .then(r => r.ok ? r.json() : null)
// //             .then(data => { if (data) setApiOptions(data); })
// //             .catch(() => { });

// //         fetchDemographics(EMPTY_FILTERS);
// //     }, []);

// //     const handleToggleValue = (filterKey, value) => {
// //         setSelected(prev => {
// //             const current = prev[filterKey] || [];
// //             return {
// //                 ...prev,
// //                 [filterKey]: current.includes(value)
// //                     ? current.filter(v => v !== value)
// //                     : [...current, value],
// //             };
// //         });
// //     };

// //     const handleApply = () => fetchDemographics(selected);

// //     const handleReset = () => {
// //         const empty = EMPTY_FILTERS;
// //         setSelected(empty);
// //         fetchDemographics(empty);
// //     };

// //     return (
// //         <div className="ys-wrapper">

// //             {/* ── MAIN ── */}
// //             <div className="ys-main">

// //                 {/* Page Header */}
// //                 <div className="ys-page-header">
// //                     <div>
// //                         <div className="ys-breadcrumb">Impact Dimensions</div>
// //                         <h1 className="ys-title">Youth Skilling</h1>
// //                         <p className="ys-subtitle">End-to-end tracking — outreach, training, employment, and retention outcomes</p>
// //                     </div>
// //                     <div className="ys-badge">Section 1 · Live Data</div>
// //                 </div>

// //                 {/* ── SECTION 1: Demographics ── */}
// //                 <SectionBadge n={1} label="Demographics" />
// //                 {demoLoading ? (
// //                     <div style={{ padding: "18px 0", color: "#6b7280", fontSize: 13 }}>Loading demographics…</div>
// //                 ) : (
// //                     <>
// //                         <div className="ys-cards-2">
// //                             <StatCard
// //                                 label="Gender Split"
// //                                 value={demographics && demographics.genderSplit && Object.keys(demographics.genderSplit).length > 0
// //                                     ? Object.entries(demographics.genderSplit)
// //                                         .map(([g, n]) => `${g}: ${n.toLocaleString()}`)
// //                                         .join(" / ")
// //                                     : "No data"}
// //                                 sub="Count by gender across Skilling projects"
// //                                 color="#2563eb"
// //                             />
// //                             <StatCard
// //                                 label="Avg Household Income"
// //                                 value={demographics && demographics.avgHouseholdIncome
// //                                     ? `₹${Number(demographics.avgHouseholdIncome).toLocaleString("en-IN")} / yr`
// //                                     : "No data"}
// //                                 sub="Annual household income of beneficiaries"
// //                                 color="#0e7490"
// //                             />
// //                         </div>
// //                         <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
// //                             <div className="ys-chart-title">Age Distribution of Beneficiaries</div>
// //                             <VerticalBarChart data={ageDistribution} nameKey="range" height={160} color1="#5b21b6" color2="#a78bfa" />
// //                         </div>
// //                     </>
// //                 )}

// //                 {/* ── SECTION 2: Outreach Funnel ── */}
// //                 <SectionBadge n={2} label="Outreach Funnel" />
// //                 <div className="ys-cards-4">
// //                     {OUTREACH.map(d => <StatCard key={d.label} {...d} />)}
// //                 </div>

// //                 {/* ── SECTION 3: Training Activities ── */}
// //                 {/* <SectionBadge n={3} label="Training Activities (HOI)" />
// //                 <div className="ys-cards-3">
// //                     {TRAINING_ACTIVITIES.map(d => <StatCard key={d.label} {...d} />)}
// //                 </div> */}

// //                 {/* ── SECTION 4: Training Outcomes ── */}
// //                 <SectionBadge n={4} label="Training Outcomes" />
// //                 <div className="ys-cards-4">
// //                     {TRAINING_OUTCOMES_CARDS.map(d => <StatCard key={d.label} {...d} />)}
// //                 </div>

// //                 {/* Sectors + Job Roles bar chart just below */}
// //                 <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
// //                     <div className="ys-chart-row">
// //                         {/* Mini KPI for sectors */}
// //                         <div className="ys-sectors-kpi">
// //                             <div className="ys-mini-label">Sectors Trained</div>
// //                             <div className="ys-mini-val" style={{ color: SECTORS_TRAINED.color }}>
// //                                 {SECTORS_TRAINED.value}
// //                             </div>
// //                             <div className="ys-mini-sub">{SECTORS_TRAINED.sub}</div>
// //                         </div>

// //                         <div className="ys-divider-v" />

// //                         {/* Bar chart for job roles */}
// //                         <div style={{ flex: 1 }}>
// //                             <div className="ys-chart-title">Number of Beneficiaries Trained by Sector</div>
// //                             <VerticalBarChart data={JOB_ROLES_DATA} height={190} />
// //                         </div>
// //                     </div>
// //                 </div>

// //                 {/* ── SECTION 5: Employment Outcomes ── */}
// //                 <SectionBadge n={5} label="Employment Outcomes" />
// //                 <div className="ys-cards-4">
// //                     {EMPLOYMENT_CARDS.map(d => <StatCard key={d.label} {...d} />)}
// //                 </div>

// //                 {/* Salary distribution bar chart */}
// //                 <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
// //                     <div className="ys-chart-title">Salary Distribution Brackets at Placement</div>
// //                     <div className="ys-chart-note">Categories: &lt;₹10K · ₹10–15K · ₹15–20K · ₹20–25K · &gt;₹25K</div>
// //                     <VerticalBarChart data={SALARY_DIST} nameKey="range" height={160} color1="#065f46" color2="#34d399" />
// //                 </div>

// //                 {/* Job sectors bar chart */}
// //                 <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
// //                     <div className="ys-chart-title">Placed Beneficiaries by Job Sector</div>
// //                     <VerticalBarChart data={JOB_SECTORS} height={170} color1="#7c2d12" color2="#fb923c" />
// //                 </div>

// //                 {/* ── SECTION 6: Retention & Career Progression ── */}
// //                 <SectionBadge n={6} label="Retention & Career Progression" />
// //                 <div className="ys-cards-4">
// //                     {RETENTION_ROW1.map(d => <StatCard key={d.label} {...d} />)}
// //                 </div>
// //                 <div className="ys-cards-1" style={{ marginTop: 12 }}>
// //                     {RETENTION_ROW2.map(d => <StatCard key={d.label} {...d} />)}
// //                 </div>

// //                 {/* ── SECTION 7: Program Experience ── */}
// //                 <SectionBadge n={7} label="Program Experience" />
// //                 <div className="ys-cards-2">
// //                     {PROGRAM_EXP.map(d => <StatCard key={d.label} {...d} />)}
// //                 </div>

// //             </div>

// //             {/* ── FILTER SIDEBAR ── */}
// //             <FilterSidebar
// //                 selected={selected}
// //                 onToggleValue={handleToggleValue}
// //                 onApply={handleApply}
// //                 onReset={handleReset}
// //                 apiOptions={apiOptions}
// //             />

// //             <style>{`
// // .ys-wrapper {
// //     display: flex;
// //     align-items: flex-start;
// //     background: #f8fafc;
// //     min-height: 100vh;
// //     font-family: Inter, system-ui, sans-serif;
// //     color: #111827;
// // }
// // .ys-main { flex: 1; min-width: 0; padding: 24px 28px; }

// // /* Page header */
// // .ys-page-header {
// //     display: flex; justify-content: space-between; align-items: flex-start;
// //     margin-bottom: 20px;
// // }
// // .ys-breadcrumb { font-size: 12px; color: #6b7280; font-weight: 700; margin-bottom: 4px; }
// // .ys-title { font-size: 22px; font-weight: 900; color: #0f172a; margin: 0 0 4px; }
// // .ys-subtitle { font-size: 13px; color: #64748b; }
// // .ys-badge {
// //     background: #dcfce7; color: #166534;
// //     font-size: 11px; font-weight: 800; padding: 5px 12px;
// //     border-radius: 999px; white-space: nowrap;
// // }

// // /* Section header */
// // .ys-section-hd {
// //     display: flex; align-items: center; gap: 10px;
// //     margin: 22px 0 10px;
// // }
// // .ys-section-badge {
// //     background: #065f46; color: #fff;
// //     font-size: 10px; font-weight: 900; padding: 3px 9px;
// //     border-radius: 999px; white-space: nowrap; letter-spacing: 0.4px;
// // }
// // .ys-section-title {
// //     font-size: 14px; font-weight: 900; color: #0f172a;
// // }

// // /* Card grids */
// // .ys-cards-1 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 12px; }
// // .ys-cards-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
// // .ys-cards-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
// // .ys-cards-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }

// // /* Stat card */
// // .ys-card {
// //     background: #fff; border: 1px solid #e5e7eb;
// //     border-radius: 12px; padding: 16px 14px;
// //     box-shadow: 0 1px 3px rgba(0,0,0,0.05);
// // }
// // .ys-stat { }
// // .ys-stat-val { font-size: 24px; font-weight: 900; line-height: 1; margin-bottom: 6px; }
// // .ys-stat-lbl { font-size: 12px; font-weight: 800; color: #374151; margin-bottom: 3px; }
// // .ys-stat-sub { font-size: 11px; color: #9ca3af; line-height: 1.3; }

// // /* Chart card */
// // .ys-chart-card { padding: 18px 20px; }
// // .ys-chart-title { font-size: 13px; font-weight: 800; color: #1e293b; margin-bottom: 4px; }
// // .ys-chart-note  { font-size: 11px; color: #9ca3af; margin-bottom: 14px; }

// // .ys-chart-row { display: flex; align-items: stretch; gap: 20px; }
// // .ys-sectors-kpi {
// //     display: flex; flex-direction: column; justify-content: center;
// //     align-items: center; width: 130px; flex-shrink: 0; text-align: center;
// // }
// // .ys-mini-label { font-size: 11px; font-weight: 800; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
// // .ys-mini-val   { font-size: 40px; font-weight: 900; line-height: 1; }
// // .ys-mini-sub   { font-size: 11px; color: #9ca3af; margin-top: 6px; }
// // .ys-divider-v  { width: 1px; background: #e5e7eb; flex-shrink: 0; }

// // /* Vertical bar chart */
// // .ys-vbar-wrap {
// //     display: flex; gap: 10px; align-items: flex-end; padding-bottom: 8px;
// // }
// // .ys-vbar-item {
// //     flex: 1; display: flex; flex-direction: column;
// //     align-items: center; gap: 4px; height: 100%;
// // }
// // .ys-vbar-val  { font-size: 10px; font-weight: 800; color: #374151; }
// // .ys-vbar-track {
// //     flex: 1; width: 100%; max-width: 56px;
// //     background: #f1f5f9; border-radius: 6px 6px 0 0;
// //     display: flex; align-items: flex-end; overflow: hidden;
// // }
// // .ys-vbar-fill { width: 100%; border-radius: 6px 6px 0 0; transition: height 0.4s ease; }
// // .ys-vbar-name {
// //     font-size: 10px; font-weight: 700; color: #6b7280;
// //     text-align: center; line-height: 1.3; max-width: 72px;
// // }
// //             `}</style>
// //         </div>
// //     );
// // }




// // // import React, { useState, useEffect } from "react";

// // // const BASE_URL_DASH = "https://mis.empowerplus.in";

// // // /* ── FILTER DEFINITIONS ── */
// // // const FILTER_DEFS = [
// // //     { key: "fy", label: "FY", type: "multi", apiKey: "fy" },
// // //     { key: "date", label: "Date", type: "daterange" },
// // //     { key: "fundingSource", label: "Funding Source", type: "multi", apiKey: "fundingSource" },
// // //     { key: "scheme", label: "Scheme", type: "hierarchical", apiKey: "schemes" },
// // //     { key: "subScheme", label: "Sub Scheme", type: "hierarchical", apiKey: "subSchemes" },
// // //     { key: "project", label: "Project", type: "hierarchical", apiKey: "projects" },
// // //     { key: "phase", label: "Phase", type: "multi", apiKey: "phases" },
// // //     { key: "state", label: "State", type: "hierarchical", apiKey: "states" },
// // //     { key: "district", label: "District", type: "hierarchical", apiKey: "districts" },
// // //     { key: "centre", label: "Centre", type: "hierarchical", apiKey: "centers" },
// // //     { key: "funder", label: "Funder", type: "multi", apiKey: "funders" },
// // //     { key: "gender", label: "Gender", type: "multi", staticOptions: ["Male", "Female", "Transgender"] },
// // // ];

// // // const FLT_DEFAULT_OPEN = { fy: true, subScheme: true, state: true };
// // // const EMPTY_FILTERS = { fy: [], fundingSource: [], scheme: [], subScheme: [], project: [], phase: [], state: [], district: [], centre: [], funder: [], gender: [] };

// // // /* ── FILTER SIDEBAR ── */
// // // function FilterGroup({ filter, open, onToggle, options, selected, onToggleValue }) {
// // //     return (
// // //         <div className="flt-group">
// // //             <button className="flt-group-hd" onClick={onToggle}>
// // //                 <span className="flt-group-lbl">{filter.label}</span>
// // //                 <span className={`flt-chevron${open ? " open" : ""}`}>›</span>
// // //             </button>
// // //             {open && (
// // //                 <div className="flt-body">
// // //                     {filter.type === "daterange" && (
// // //                         <div className="flt-daterange">
// // //                             <label>Start</label>
// // //                             <input type="date" className="flt-date-input" />
// // //                             <label>End</label>
// // //                             <input type="date" className="flt-date-input" />
// // //                         </div>
// // //                     )}
// // //                     {(filter.type === "multi" || filter.type === "hierarchical") && options.map(opt => (
// // //                         <label key={opt} className="flt-check-row">
// // //                             <input
// // //                                 type="checkbox"
// // //                                 className="flt-cb"
// // //                                 checked={selected.includes(opt)}
// // //                                 onChange={() => onToggleValue(filter.key, opt)}
// // //                             />
// // //                             <span>{opt}</span>
// // //                         </label>
// // //                     ))}
// // //                 </div>
// // //             )}
// // //         </div>
// // //     );
// // // }

// // // function FilterSidebar({ selected, onToggleValue, onApply, onReset, apiOptions }) {
// // //     const [openFilters, setOpenFilters] = useState(FLT_DEFAULT_OPEN);

// // //     const getOptions = (filter) => {
// // //         if (filter.staticOptions) return filter.staticOptions;
// // //         if (apiOptions && filter.apiKey && Array.isArray(apiOptions[filter.apiKey]))
// // //             return apiOptions[filter.apiKey];
// // //         return [];
// // //     };

// // //     const toggle = (key) => setOpenFilters(prev => ({ ...prev, [key]: !prev[key] }));
// // //     const totalSelected = Object.values(selected).flat().length;

// // //     return (
// // //         <aside className="flt-sidebar">
// // //             <div className="flt-header">
// // //                 <span className="flt-title">Filters {totalSelected > 0 && <span className="flt-badge">{totalSelected}</span>}</span>
// // //                 <button className="flt-reset" onClick={onReset}>Reset All</button>
// // //             </div>
// // //             <div className="flt-note">All filter views change according to selection.</div>
// // //             {FILTER_DEFS.map(filter => (
// // //                 <FilterGroup
// // //                     key={filter.key}
// // //                     filter={filter}
// // //                     open={!!openFilters[filter.key]}
// // //                     onToggle={() => toggle(filter.key)}
// // //                     options={getOptions(filter)}
// // //                     selected={selected[filter.key] || []}
// // //                     onToggleValue={onToggleValue}
// // //                 />
// // //             ))}
// // //             <button className="flt-apply" onClick={onApply}>Apply Filters</button>
// // //             <style>{`
// // // .flt-sidebar { width:256px; flex-shrink:0; background:#fff; border-left:1px solid #e5e7eb; padding:16px 14px 24px; min-height:100vh; position:sticky; top:0; max-height:100vh; overflow-y:auto; font-family:Inter,system-ui,sans-serif; }
// // // .flt-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
// // // .flt-title { font-size:14px; font-weight:900; color:#0f172a; display:flex; align-items:center; gap:6px; }
// // // .flt-badge { background:#2563eb; color:#fff; font-size:10px; font-weight:800; border-radius:999px; padding:1px 6px; }
// // // .flt-reset { font-size:11px; font-weight:700; color:#6b7280; background:none; border:1px solid #e5e7eb; border-radius:6px; padding:3px 8px; cursor:pointer; }
// // // .flt-reset:hover { color:#ef4444; border-color:#fca5a5; }
// // // .flt-note { font-size:10px; color:#9ca3af; line-height:1.4; background:#f8fafc; border-radius:6px; padding:6px 8px; margin-bottom:12px; }
// // // .flt-group { border-bottom:1px solid #f1f5f9; }
// // // .flt-group-hd { display:flex; align-items:center; justify-content:space-between; width:100%; background:none; border:none; padding:9px 2px; cursor:pointer; font-size:12px; font-weight:800; color:#374151; text-align:left; }
// // // .flt-group-hd:hover { color:#2563eb; }
// // // .flt-group-lbl { flex:1; }
// // // .flt-chevron { font-size:14px; color:#9ca3af; transform:rotate(90deg); display:inline-block; transition:transform 0.2s; line-height:1; }
// // // .flt-chevron.open { transform:rotate(270deg); }
// // // .flt-body { padding:4px 0 10px 4px; display:flex; flex-direction:column; gap:4px; max-height:180px; overflow-y:auto; }
// // // .flt-check-row { display:flex; align-items:center; gap:7px; font-size:12px; color:#4b5563; cursor:pointer; padding:2px 4px; border-radius:5px; }
// // // .flt-check-row:hover { background:#eff6ff; color:#2563eb; }
// // // .flt-cb { accent-color:#2563eb; width:13px; height:13px; flex-shrink:0; cursor:pointer; }
// // // .flt-daterange { display:flex; flex-direction:column; gap:5px; font-size:11px; font-weight:700; color:#6b7280; }
// // // .flt-date-input { border:1px solid #e5e7eb; border-radius:6px; padding:5px 7px; font-size:11px; color:#374151; outline:none; }
// // // .flt-date-input:focus { border-color:#2563eb; }
// // // .flt-apply { width:100%; margin-top:16px; background:#2563eb; color:#fff; border:none; border-radius:10px; padding:10px; font-size:13px; font-weight:800; cursor:pointer; }
// // // .flt-apply:hover { background:#1d4ed8; }
// // //             `}</style>
// // //         </aside>
// // //     );
// // // }

// // // /* ─── STATIC DATA ─── */

// // // const DEMOGRAPHICS = [
// // //     { label: "Gender Split", value: "68% M / 32% F", sub: "Male-female ratio across all batches", color: "#2563eb" },
// // //     { label: "Avg Household Income", value: "₹1.2 L / yr", sub: "Annual household income of beneficiaries", color: "#0e7490" },
// // // ];

// // // const AGE_DIST = [
// // //     { range: "Below 18", count: 980 },
// // //     { range: "18 to 25", count: 8420 },
// // //     { range: "25 to 35", count: 4320 },
// // //     { range: "35+",      count: 860 },
// // // ];

// // // const OUTREACH = [
// // //     { label: "Candidates Mobilised", value: "24,500", sub: "Total outreach contacts", color: "#2563eb" },
// // //     { label: "Candidates Counseled", value: "18,200", sub: "74.3% of mobilised", color: "#7c3aed" },
// // //     { label: "Candidates Enrolled", value: "15,600", sub: "85.7% of counseled", color: "#059669" },
// // //     { label: "Dropout Rate", value: "8.4%", sub: "Post-enrolment dropout", color: "#ef4444" },
// // // ];

// // // const TRAINING_ACTIVITIES = [
// // //     { label: "Guest Lectures Organised", value: "142", sub: "Across all active centres", color: "#2563eb" },
// // //     { label: "Alumni Interactions Organised", value: "68", sub: "Peer learning sessions", color: "#7c3aed" },
// // //     { label: "Industry Visits Organised", value: "53", sub: "Factory / site visits", color: "#0e7490" },
// // // ];

// // // const TRAINING_OUTCOMES_CARDS = [
// // //     { label: "Candidates Trained", value: "14,380", sub: "Completed full training", color: "#2563eb" },
// // //     { label: "Candidates Assessed", value: "13,210", sub: "91.9% of trained", color: "#7c3aed" },
// // //     { label: "Candidates Certified", value: "11,890", sub: "Passed assessment", color: "#059669" },
// // //     { label: "Certification Rate", value: "89.6%", sub: "Certified ÷ Assessed", color: "#f59e0b" },
// // // ];

// // // const SECTORS_TRAINED = { label: "Sectors Trained", value: "12", sub: "Distinct job sectors covered", color: "#0e7490" };

// // // const JOB_ROLES_DATA = [
// // //     { name: "Electrician", count: 2840 },
// // //     { name: "Welder", count: 2310 },
// // //     { name: "BFSI Executive", count: 1980 },
// // //     { name: "Retail Associate", count: 1760 },
// // //     { name: "IT Support", count: 1450 },
// // //     { name: "Hospitality", count: 1240 },
// // //     { name: "Solar Technician", count: 980 },
// // //     { name: "Healthcare Aid", count: 820 },
// // // ];

// // // const EMPLOYMENT_CARDS = [
// // //     { label: "Candidates Placed", value: "9,840", sub: "Successfully employed", color: "#2563eb" },
// // //     { label: "Placement Rate", value: "68.4%", sub: "Placed ÷ Trained", color: "#7c3aed" },
// // //     { label: "Avg Salary at Placement", value: "₹14,200", sub: "Per month at joining", color: "#059669" },
// // //     { label: "Total Income Generated (Annual)", value: "₹168 Cr", sub: "Aggregate annual income of placed", color: "#f59e0b" },
// // // ];

// // // const SALARY_DIST = [
// // //     { range: "< ₹10K", count: 820 },
// // //     { range: "₹10–15K", count: 3240 },
// // //     { range: "₹15–20K", count: 3680 },
// // //     { range: "₹20–25K", count: 1620 },
// // //     { range: "> ₹25K", count: 480 },
// // // ];

// // // const JOB_SECTORS = [
// // //     { name: "Manufacturing", count: 2840 },
// // //     { name: "Retail", count: 2190 },
// // //     { name: "IT / ITES", count: 1680 },
// // //     { name: "Construction", count: 1340 },
// // //     { name: "Healthcare", count: 980 },
// // //     { name: "Hospitality", count: 810 },
// // // ];

// // // const RETENTION_ROW1 = [
// // //     { label: "% Retained after 3 months", value: "84%", sub: "Active at 3m ÷ Placed · see formula", color: "#2563eb" },
// // //     { label: "% Retained after 6 months", value: "71%", sub: "Active at 6m ÷ Placed", color: "#7c3aed" },
// // //     { label: "% Retained after 12 months", value: "58%", sub: "Active at 12m ÷ Placed", color: "#059669" },
// // //     { label: "Avg Salary Growth after 1 yr", value: "+22%", sub: "Salary at 1yr vs. joining salary", color: "#f59e0b" },
// // // ];

// // // const RETENTION_ROW2 = [
// // //     { label: "% Promoted / Added Responsibility", value: "34%", sub: "Of 12-month retained cohort", color: "#0e7490" },
// // // ];

// // // const PROGRAM_EXP = [
// // //     { label: "Training Satisfaction Score", value: "4.2 / 5", sub: "Avg rating from exit surveys", color: "#2563eb" },
// // //     { label: "Recommendation Score", value: "82%", sub: "Would recommend to peers", color: "#059669" },
// // // ];

// // // /* ─── HELPERS ─── */

// // // function SectionBadge({ n, label }) {
// // //     return (
// // //         <div className="ys-section-hd">
// // //             <span className="ys-section-badge">Section {n}</span>
// // //             <span className="ys-section-title">{label}</span>
// // //         </div>
// // //     );
// // // }

// // // function StatCard({ label, value, sub, color }) {
// // //     return (
// // //         <div className="ys-card ys-stat" style={{ borderTop: `4px solid ${color}` }}>
// // //             <div className="ys-stat-val" style={{ color }}>{value}</div>
// // //             <div className="ys-stat-lbl">{label}</div>
// // //             {sub && <div className="ys-stat-sub">{sub}</div>}
// // //         </div>
// // //     );
// // // }

// // // function VerticalBarChart({ data, nameKey = "name", valueKey = "count", height = 180, color1 = "#1e40af", color2 = "#60a5fa" }) {
// // //     const max = Math.max(...data.map(d => d[valueKey]));
// // //     return (
// // //         <div className="ys-vbar-wrap" style={{ height }}>
// // //             {data.map(d => (
// // //                 <div key={d[nameKey]} className="ys-vbar-item">
// // //                     <div className="ys-vbar-val">{d[valueKey].toLocaleString()}</div>
// // //                     <div className="ys-vbar-track">
// // //                         <div className="ys-vbar-fill"
// // //                             style={{
// // //                                 height: `${(d[valueKey] / max) * 100}%`,
// // //                                 background: `linear-gradient(to top, ${color1}, ${color2})`,
// // //                             }}
// // //                         />
// // //                     </div>
// // //                     <div className="ys-vbar-name">{d[nameKey]}</div>
// // //                 </div>
// // //             ))}
// // //         </div>
// // //     );
// // // }

// // // /* ─── PAGE ─── */

// // // export default function YouthSkillingPage() {
// // //     const [selected, setSelected] = useState(EMPTY_FILTERS);
// // //     const [apiOptions, setApiOptions] = useState(null);

// // //     useEffect(() => {
// // //         const token = localStorage.getItem("token");
// // //         fetch(`${BASE_URL_DASH}/api/view-dashboard/filter-options`, {
// // //             headers: { Authorization: `Bearer ${token}` },
// // //         })
// // //             .then(r => r.ok ? r.json() : null)
// // //             .then(data => { if (data) setApiOptions(data); })
// // //             .catch(() => {});
// // //     }, []);

// // //     const handleToggleValue = (filterKey, value) => {
// // //         setSelected(prev => {
// // //             const current = prev[filterKey] || [];
// // //             return {
// // //                 ...prev,
// // //                 [filterKey]: current.includes(value)
// // //                     ? current.filter(v => v !== value)
// // //                     : [...current, value],
// // //             };
// // //         });
// // //     };

// // //     const handleReset = () => setSelected(EMPTY_FILTERS);

// // //     return (
// // //         <div className="ys-wrapper">

// // //             {/* ── MAIN ── */}
// // //             <div className="ys-main">

// // //                 {/* Page Header */}
// // //                 <div className="ys-page-header">
// // //                     <div>
// // //                         <div className="ys-breadcrumb">Impact Dimensions</div>
// // //                         <h1 className="ys-title">Youth Skilling</h1>
// // //                         <p className="ys-subtitle">End-to-end tracking — outreach, training, employment, and retention outcomes</p>
// // //                     </div>
// // //                     <div className="ys-badge">Static · FY 2024–25</div>
// // //                 </div>

// // //                 {/* ── SECTION 1: Demographics ── */}
// // //                 <SectionBadge n={1} label="Demographics" />
// // //                 <div className="ys-cards-2">
// // //                     {DEMOGRAPHICS.map(d => <StatCard key={d.label} {...d} />)}
// // //                 </div>
// // //                 <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
// // //                     <div className="ys-chart-title">Age Distribution of Beneficiaries</div>
// // //                     <VerticalBarChart data={AGE_DIST} nameKey="range" height={160} color1="#5b21b6" color2="#a78bfa" />
// // //                 </div>

// // //                 {/* ── SECTION 2: Outreach Funnel ── */}
// // //                 <SectionBadge n={2} label="Outreach Funnel" />
// // //                 <div className="ys-cards-4">
// // //                     {OUTREACH.map(d => <StatCard key={d.label} {...d} />)}
// // //                 </div>

// // //                 {/* ── SECTION 3: Training Activities ── */}
// // //                 <SectionBadge n={3} label="Training Activities (HOI)" />
// // //                 <div className="ys-cards-3">
// // //                     {TRAINING_ACTIVITIES.map(d => <StatCard key={d.label} {...d} />)}
// // //                 </div>

// // //                 {/* ── SECTION 4: Training Outcomes ── */}
// // //                 <SectionBadge n={4} label="Training Outcomes" />
// // //                 <div className="ys-cards-4">
// // //                     {TRAINING_OUTCOMES_CARDS.map(d => <StatCard key={d.label} {...d} />)}
// // //                 </div>

// // //                 {/* Sectors + Job Roles bar chart just below */}
// // //                 <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
// // //                     <div className="ys-chart-row">
// // //                         {/* Mini KPI for sectors */}
// // //                         <div className="ys-sectors-kpi">
// // //                             <div className="ys-mini-label">Sectors Trained</div>
// // //                             <div className="ys-mini-val" style={{ color: SECTORS_TRAINED.color }}>
// // //                                 {SECTORS_TRAINED.value}
// // //                             </div>
// // //                             <div className="ys-mini-sub">{SECTORS_TRAINED.sub}</div>
// // //                         </div>

// // //                         <div className="ys-divider-v" />

// // //                         {/* Bar chart for job roles */}
// // //                         <div style={{ flex: 1 }}>
// // //                             <div className="ys-chart-title">Number of Beneficiaries Trained by Sector</div>
// // //                             <VerticalBarChart data={JOB_ROLES_DATA} height={190} />
// // //                         </div>
// // //                     </div>
// // //                 </div>

// // //                 {/* ── SECTION 5: Employment Outcomes ── */}
// // //                 <SectionBadge n={5} label="Employment Outcomes" />
// // //                 <div className="ys-cards-4">
// // //                     {EMPLOYMENT_CARDS.map(d => <StatCard key={d.label} {...d} />)}
// // //                 </div>

// // //                 {/* Salary distribution bar chart */}
// // //                 <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
// // //                     <div className="ys-chart-title">Salary Distribution Brackets at Placement</div>
// // //                     <div className="ys-chart-note">Categories: &lt;₹10K · ₹10–15K · ₹15–20K · ₹20–25K · &gt;₹25K</div>
// // //                     <VerticalBarChart data={SALARY_DIST} nameKey="range" height={160} color1="#065f46" color2="#34d399" />
// // //                 </div>

// // //                 {/* Job sectors bar chart */}
// // //                 <div className="ys-card ys-chart-card" style={{ marginTop: 12 }}>
// // //                     <div className="ys-chart-title">Placed Beneficiaries by Job Sector</div>
// // //                     <VerticalBarChart data={JOB_SECTORS} height={170} color1="#7c2d12" color2="#fb923c" />
// // //                 </div>

// // //                 {/* ── SECTION 6: Retention & Career Progression ── */}
// // //                 <SectionBadge n={6} label="Retention & Career Progression" />
// // //                 <div className="ys-cards-4">
// // //                     {RETENTION_ROW1.map(d => <StatCard key={d.label} {...d} />)}
// // //                 </div>
// // //                 <div className="ys-cards-1" style={{ marginTop: 12 }}>
// // //                     {RETENTION_ROW2.map(d => <StatCard key={d.label} {...d} />)}
// // //                 </div>

// // //                 {/* ── SECTION 7: Program Experience ── */}
// // //                 <SectionBadge n={7} label="Program Experience" />
// // //                 <div className="ys-cards-2">
// // //                     {PROGRAM_EXP.map(d => <StatCard key={d.label} {...d} />)}
// // //                 </div>

// // //             </div>

// // //             {/* ── FILTER SIDEBAR ── */}
// // //             <FilterSidebar
// // //                 selected={selected}
// // //                 onToggleValue={handleToggleValue}
// // //                 onApply={() => {}}
// // //                 onReset={handleReset}
// // //                 apiOptions={apiOptions}
// // //             />

// // //             <style>{`
// // // .ys-wrapper {
// // //     display: flex;
// // //     align-items: flex-start;
// // //     background: #f8fafc;
// // //     min-height: 100vh;
// // //     font-family: Inter, system-ui, sans-serif;
// // //     color: #111827;
// // // }
// // // .ys-main { flex: 1; min-width: 0; padding: 24px 28px; }

// // // /* Page header */
// // // .ys-page-header {
// // //     display: flex; justify-content: space-between; align-items: flex-start;
// // //     margin-bottom: 20px;
// // // }
// // // .ys-breadcrumb { font-size: 12px; color: #6b7280; font-weight: 700; margin-bottom: 4px; }
// // // .ys-title { font-size: 22px; font-weight: 900; color: #0f172a; margin: 0 0 4px; }
// // // .ys-subtitle { font-size: 13px; color: #64748b; }
// // // .ys-badge {
// // //     background: #dcfce7; color: #166534;
// // //     font-size: 11px; font-weight: 800; padding: 5px 12px;
// // //     border-radius: 999px; white-space: nowrap;
// // // }

// // // /* Section header */
// // // .ys-section-hd {
// // //     display: flex; align-items: center; gap: 10px;
// // //     margin: 22px 0 10px;
// // // }
// // // .ys-section-badge {
// // //     background: #065f46; color: #fff;
// // //     font-size: 10px; font-weight: 900; padding: 3px 9px;
// // //     border-radius: 999px; white-space: nowrap; letter-spacing: 0.4px;
// // // }
// // // .ys-section-title {
// // //     font-size: 14px; font-weight: 900; color: #0f172a;
// // // }

// // // /* Card grids */
// // // .ys-cards-1 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 12px; }
// // // .ys-cards-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
// // // .ys-cards-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
// // // .ys-cards-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }

// // // /* Stat card */
// // // .ys-card {
// // //     background: #fff; border: 1px solid #e5e7eb;
// // //     border-radius: 12px; padding: 16px 14px;
// // //     box-shadow: 0 1px 3px rgba(0,0,0,0.05);
// // // }
// // // .ys-stat { }
// // // .ys-stat-val { font-size: 24px; font-weight: 900; line-height: 1; margin-bottom: 6px; }
// // // .ys-stat-lbl { font-size: 12px; font-weight: 800; color: #374151; margin-bottom: 3px; }
// // // .ys-stat-sub { font-size: 11px; color: #9ca3af; line-height: 1.3; }

// // // /* Chart card */
// // // .ys-chart-card { padding: 18px 20px; }
// // // .ys-chart-title { font-size: 13px; font-weight: 800; color: #1e293b; margin-bottom: 4px; }
// // // .ys-chart-note  { font-size: 11px; color: #9ca3af; margin-bottom: 14px; }

// // // .ys-chart-row { display: flex; align-items: stretch; gap: 20px; }
// // // .ys-sectors-kpi {
// // //     display: flex; flex-direction: column; justify-content: center;
// // //     align-items: center; width: 130px; flex-shrink: 0; text-align: center;
// // // }
// // // .ys-mini-label { font-size: 11px; font-weight: 800; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
// // // .ys-mini-val   { font-size: 40px; font-weight: 900; line-height: 1; }
// // // .ys-mini-sub   { font-size: 11px; color: #9ca3af; margin-top: 6px; }
// // // .ys-divider-v  { width: 1px; background: #e5e7eb; flex-shrink: 0; }

// // // /* Vertical bar chart */
// // // .ys-vbar-wrap {
// // //     display: flex; gap: 10px; align-items: flex-end; padding-bottom: 8px;
// // // }
// // // .ys-vbar-item {
// // //     flex: 1; display: flex; flex-direction: column;
// // //     align-items: center; gap: 4px; height: 100%;
// // // }
// // // .ys-vbar-val  { font-size: 10px; font-weight: 800; color: #374151; }
// // // .ys-vbar-track {
// // //     flex: 1; width: 100%; max-width: 56px;
// // //     background: #f1f5f9; border-radius: 6px 6px 0 0;
// // //     display: flex; align-items: flex-end; overflow: hidden;
// // // }
// // // .ys-vbar-fill { width: 100%; border-radius: 6px 6px 0 0; transition: height 0.4s ease; }
// // // .ys-vbar-name {
// // //     font-size: 10px; font-weight: 700; color: #6b7280;
// // //     text-align: center; line-height: 1.3; max-width: 72px;
// // // }
// // //             `}</style>
// // //         </div>
// // //     );
// // // }
